import * as d3 from 'd3';
// @ts-expect-error this import works
import debounce from 'lodash.debounce';
import {
	ClusterBoundElement,
	NCNodeDefinition,
	GraphProps,
	LinkBoundElement,
	MenuOption,
	NodeBoundElement,
} from '../../types';
import {
	ButtonDefinition,
	clearNonBlockingMessage,
	clearNonBlockingMessageWithMessage,
	createButtons,
	createDialog,
	getUserByColour,
	showAlert,
	showNonBlockingMessage,
	updateUsersOnlineStatus,
} from '../ui/uiUtils';
import {
	getParent,
	getDistanceForConnection,
	createSvg,
	calcGraphWidthAndHeight,
	getLabels,
	getAllLinkedNodes,
	findClosestNodeToLinkTo,
	getUpdatedNodesLinksGroups,
	getMouseTargetInfo,
	updateCursorAndMouseLink,
	updateMouseLink,
	createMouseLink,
	createCustomCursor,
	getRadialMenuButtonArc,
} from './graphUtils';
import { addToNodeList, constructNodeFromDefinition, isSameNode } from './nodeUtils';
import { buildNodeContextPath } from './nodeContextPath';
import { isUserInitiatedNodeActivation } from './nodeActivationSource';
import { isNodeLikedByUser, nodeUserLikeKey, withNodeLikeState } from './nodeLikes';
import { resolveNodeUidFromEventTarget } from './resolveNodeFromEvent';
import { isPdfGraphNode } from '../../../../src/services/pdf/pdfNodeEligibility';
import {
	createNodes,
	updateLabelInput,
	updateNodeAndMenu,
	enterNode,
	generateNodeId,
	getPdfDisplayName,
	updateNodePositions,
	updateNodes,
} from './graphNodeUtils';
import { setPresentationLink } from '../ui/slidesUI';
import Zoomable from './Zoomable';
import * as toolsUi from '../ui/toolsUI';
import * as searchUi from '../ui/searchUi';
import * as generateUi from '../ui/generateUi';
import * as sparkUi from '../ui/sparkUi';
import * as empathiseUi from '../ui/empathiseUi';
import { getSynthesiseOutputTitle } from '../ui/synthesiseOutputTitle';
import * as ncApi from '../apiRequests';
import * as tutorial from '../tutorials/tutorial';
import * as templateTutorial from '../tutorials/templateTutorial';
import * as tutorialEvents from '../tutorials/tutorialEvents';
import { createLinkLines, updateActiveLink, updateLinkPositions } from './graphLinkUtils';
import { createClusterNodes } from './graphClusterUtils';
import MoodboardGraph from './MoodboardGraph';
import InspireGraph from './InspireGraph';
import IdeateGraph from './IdeateGraph';
import { ANALYSES_TYPES } from '../../../../src/commonConstants';
import { refreshHeartedList } from '../ui/ideaBox';
import {
	addNodeToPersonalMoodboard,
	removeNodeFromPersonalMoodboard,
	setInspireNodeLike,
} from '../shareDbUtils';
import { FilterOptions, setTutorialShown, splitTextIntoWords } from '../apiRequests';
import {
	AvailableColour,
	NCCluster,
	NCGraphData,
	NCLink,
	NCNode,
	BoardUser,
	GraphMode,
	UserSelection,
	Point,
	NCGroup,
	LikedItem,
	Motivations,
	UserPersona,
	EmpathiseColour,
	TopicAnalysis,
	UserFile,
	GenerateTask,
	SearchMode,
	PositioningLayout,
	PositioningOutputSnapshot,
} from '../../../../src/commonTypes';
import {
	showCommonIdeasNodesPrompt,
	showCreateGroupPrompt,
	showLinkingNodesPrompt,
	showSynthesiseNodesPrompt,
	toggleGroupEditControls,
	updateInspireProgress,
} from '../ui/toolsUI';
import {
	findFreeSpace,
	findFreeSpaceRadial,
	getCentralPoint,
	getDistanceBetweenPoints,
} from './geometryUtils';
import {
	confirmRemoveGroup,
	createGroups,
	getRandomGroupColor,
	updateGroupPositions,
} from './graphGroupUtils';
import { initPhysics, resetPhysicsMovement } from './graphPhysics';
import { splitNodes } from '../../mergeSplitUtils';
import { initNodeDragBehavior, initGroupDragBehavior } from './dragUtils';
import { capitalizeFirstLetter, splitWords, userPersonaToString } from '../../../../src/commonMisc';
import { showSingleImageLightbox } from '../ui/lightbox';
import { getLinksInvolvingNode, isSameLink, minimizeLinks } from './linkUtils';
import { getNewNodeLinkTargets } from './nodeLinkTargets';
import {
	getGroupsWhereNodeIsOnlyNode,
	getNodesWithinGroupArea,
	minimizeGroups,
} from './groupUtils';
import { disableChatbot, enableChatbot } from '../tutorials/tutorialEvents';
import {
	applyDefaultGraphNodeShape,
	graphUsesRoundedRectNodes,
	isLongNodeLabel,
} from '../../../../src/graphNodeShape';
import {
	boardLabelForPositioningOutput,
	shouldReuseInspireSourceNode,
} from './ideaBoxNodeMaterialisation';
import { toReadablePositioningOutput } from '../../../../src/positioningOutputs';
import { getChangedActiveNodeIds } from './graphUserChanges';

/**
 * The Graph class performs all calculations related to the placement of nodes on the screen.
 * The graph is inserted in a div in the DOM with class '.chart' and appends a svg of a given width and height
 * to that div where all the nodes are placed in.
 */
class Graph extends Zoomable {
	protected userColourClass: AvailableColour;
	protected userColourHex: string;
	public templateId: string | undefined;
	protected aiStatus: boolean;
	public readonly isCollaborative: boolean;
	protected automation: boolean;
	protected mouse:
		| null
		| (Point & { targetId?: string; targetClass?: string; element?: HTMLElement });
	public debugMarker: null | Point;
	public nodes: Array<NCNode>;
	public links: Array<NCLink>;
	public nodeGroups: Array<NCGroup>;
	protected parentElement: d3.Selection<HTMLDivElement, null, HTMLElement, unknown>;
	protected nodeDragBehavior: d3.DragBehavior<SVGGElement, NCNode, unknown>;
	protected groupDragBehavior: d3.DragBehavior<SVGGElement, NCGroup, unknown>;
	public simulation: d3.Simulation<NCNode, NCLink> | undefined;
	protected groups: {
		nodes?: d3.Selection<SVGGElement, NCNode, SVGGElement, null>;
		links?: d3.Selection<SVGGElement, NCLink, SVGGElement, null>;
		groups?: d3.Selection<SVGGElement, NCGroup, SVGGElement, null>;
		clusters?: d3.Selection<SVGGElement, NCCluster, SVGGElement, null>;
		cursor?: d3.Selection<SVGCircleElement, null, HTMLElement, null>;
		mouselink?: d3.Selection<SVGGElement, NCNode, SVGGElement, null>;
		debugMarker?: d3.Selection<SVGCircleElement, null, HTMLElement, null>;
	};
	protected activeLink: undefined | { source: string; target: string };
	public selections = {
		connectFirstNode: undefined as NCNode | undefined,
		mergeFirstNode: undefined as NCNode | undefined,
		addToGroup: undefined as NCNode[] | undefined,
		removeFromGroup: undefined as NCNode[] | undefined,
		linkingNodes: undefined as NCNode[] | undefined,
		commonIdeasNodes: undefined as NCNode[] | undefined,
		positioningNodes: undefined as NCNode[] | undefined,
		groupingNodes: undefined as NCNode[] | undefined,
		synthesiseNodes: undefined as NCNode[] | undefined,
	};

	public lowVelocityNodesLocked = false;

	protected onlineStatusIntervalId: NodeJS.Timeout | undefined;
	public boardUsers: Array<BoardUser>;
	public userSelections: Array<UserSelection>;
	private afterLoadGraphCallback: null | (() => void) = null;
	private debouncedOnTick: () => void;
	public uploadedFiles: UserFile[] | undefined;
	public isDragging = false;
	public currentDraggedNodeCluster: Array<NCNode> | undefined;
	public draggedNodes: Array<NCNode> | undefined;
	// Needed to prompt the user to preserve or expand after clicking
	public pendingInputEvent: InputEvent | null = null;

	constructor(
		graphData: NCGraphData,
		boardUsers: Array<BoardUser>,
		userSelections: Array<UserSelection>,
		colourClass: AvailableColour,
		colourHex: string,
		isCollaborative: boolean,
		automation = false,
		createBackgroundGroups: undefined | (() => void) = undefined
	) {
		super();

		this.boardUsers = boardUsers;
		this.userSelections = userSelections;
		this.userColourClass = colourClass;
		this.userColourHex = colourHex;
		this.aiStatus = document.querySelector<HTMLInputElement>('#ai-setting')!.checked;
		this.isCollaborative = isCollaborative;
		this.automation = automation;
		this.updateGraphWithNewData(graphData);
		this.mouse = null;

		// this.parentElement refers to the <div class='chart' /> in the DOM.
		this.parentElement = d3
			.select<HTMLDivElement, null>('#board-content')
			.select<HTMLDivElement>('.chart');

		const { width, height } = calcGraphWidthAndHeight();
		// this.svg is the SVG element that is appended to the div.
		this.svg = createSvg(this.parentElement, width, height)
			// Event listeners:
			.on('mouseleave', this.onMouseLeave.bind(this))
			.on('mousemove', this.onMouseMove.bind(this))
			.on('click', this.onClick.bind(this));

		this.svg.classed(this.graphMode, true);

		this.baseElement = this.svg.append('g').attr('id', 'all-content');

		// Enable this line to see origin:
		// this.baseElement.append('rect').attr('width', 10).attr('height', 10);

		if (this.isMovable()) {
			this.nodeDragBehavior = initNodeDragBehavior(this);
			this.groupDragBehavior = initGroupDragBehavior(this);
		}

		this.groups = {};
		if (createBackgroundGroups) {
			createBackgroundGroups.call(this);
		}
		if (this.isEditable()) {
			this.groups.mouselink = createMouseLink(this.baseElement);
			this.groups.cursor = createCustomCursor(this.baseElement, this.userColourHex);
			// this.groups.debugMarker = createDebugMarker(this.baseElement);

			this.svg.attr('cursor', 'crosshair');
		}
		Object.assign(this.groups, {
			groups: createGroups(
				this.baseElement.append('g').attr('class', 'groups').selectAll('g'),
				this.nodeGroups,
				this.groupDragBehavior
			),
			links: createLinkLines(
				this.baseElement.append('g').attr('class', 'links').selectAll('g.link'),
				this.links,
				this.graphProps
			),
			nodes: createNodes(
				this.baseElement.append('g').attr('class', 'nodes').selectAll('g'),
				this.nodes,
				this.enterNode.bind(this),
				this.updateNode.bind(this),
				this.isMovable()
			),
		});

		this.onTick();

		this.simulation = initPhysics(this);
		enableChatbot();
		if (!this.automation) {
			setTimeout(() => {
				if (graphData.template && graphData.template !== 'board') {
					this.templateId = graphData.template;
					templateTutorial.start(this.templateId, this);
				} else if (
					document.body.dataset[`showTutorial${capitalizeFirstLetter(this.graphMode)}`] ===
						'true' &&
					this.nodes.length === 0
				) {
					// Start tutorial if user was not yet shown it for any board
					this.startTutorial();

					setTutorialShown(document.body.dataset.userId!, this.graphMode);
				} else {
					this.startHints();
				}
			}, 250);
		}

		tutorial.deactivateTutorialButton();

		if (this.activeNode) {
			this.updateActive(this.activeNode);
		}

		const shouldZoomToFit = this.nodes.length > 0;
		this.initZoom(shouldZoomToFit, this.automation);
		this.deleteRogueNodes();
		setTimeout(() => {
			if (shouldZoomToFit) {
				this.zoomToFit(this.automation);
			}
			toggleGroupEditControls(this);
		}, 200);

		if (!this.automation) {
			// Update user active status every 30 seconds
			this.onlineStatusIntervalId = setInterval(() => {
				this.saveUser();
			}, 30000);

			this.saveUser();
		}

		if (this.currentUser.settings.presentationId) {
			setPresentationLink(this.currentUser.settings.presentationId, this.boardTitle);
		}

		document.addEventListener('pointerdown', this.handleGlobalPointerDown, { capture: true });
	}
	dispose() {
		document.removeEventListener('pointerdown', this.handleGlobalPointerDown, { capture: true });
	}

	get boardTitle(): string {
		return document.querySelector<HTMLInputElement>('#board-title-form input')!.value;
	}

	getTemplateId() {
		return this.templateId;
	}

	setTemplateId(templateId: string) {
		this.templateId = templateId;
	}

	handleGlobalPointerDown = (_e: PointerEvent) => {
		// Needed to show the preserve and expand dialog when the user clicks
		// Always act on the globally active graph instance to avoid instance mismatches
		const g = (globalThis as any)?.neuroCreate?.graph as Graph | undefined;
		if (g?.pendingInputEvent) {
			// User was typing in the input field and clicked somewhere else
			console.log('[handleGlobalPointerDown] updating label via global graph');
			const isLineBreak = g.pendingInputEvent.inputType === 'insertLineBreak';
			if (!isLineBreak) {
				g.updateLabel(g.pendingInputEvent, true);
			}
			g.pendingInputEvent = null;
		}
	};

	// For debugging, gets midpoints of links for visualizing
	get linkMidpoints(): Array<Point> {
		return this.links.map((l) => ({
			x: (l.target.x + l.source.x) / 2,
			y: (l.target.y + l.source.y) / 2,
		}));
	}

	validate(): boolean {
		// Can be overridden to show alerts depending on state
		return true;
	}

	cleanUp() {
		clearNonBlockingMessage();
		if (this.onlineStatusIntervalId) {
			clearInterval(this.onlineStatusIntervalId);
			this.onlineStatusIntervalId = undefined;
		}
		// Always tear down the SVG so a failed/superseded mode switch cannot leave a
		// stale board painted while Flowy / hash state have already moved on.
		this.parentElement?.select<SVGSVGElement>('svg').remove();
	}

	get graphMode(): GraphMode {
		throw new Error('graphMode should be overridden');
	}

	updateGraphWithNewData(graphData: NCGraphData) {
		const { nodes, links, groups, uploadedFiles } = graphData;

		const { updatedNodes, updatedLinks, updatedGroups, missingLinks } = getUpdatedNodesLinksGroups(
			structuredClone(nodes),
			structuredClone(links),
			this.links,
			groups ? structuredClone(groups) : [],
			this.nodeGroups || []
		);
		updatedNodes.forEach((node) => applyDefaultGraphNodeShape(this.graphMode, node));

		setTimeout(() => {
			console.log('[neuroCreate.saveLinks]', neuroCreate.saveLinks);
			if (neuroCreate.saveLinks && missingLinks.length > 0) {
				neuroCreate.saveLinks.deleteMissing(missingLinks);
			}
		}, 1);
		this.nodes = updatedNodes;
		this.links = updatedLinks;
		this.rebuildLinks();
		this.nodeGroups = updatedGroups;
		this.uploadedFiles = uploadedFiles;
	}

	/**
	 * Removes the node from the board
	 */
	delete(nodeToDelete: NCNode) {
		if (nodeToDelete.colour !== this.userColourClass) {
			console.log("Cannot delete another user's node");
			return;
		}

		if (this.nodes.some((node) => node.uid === nodeToDelete?.uid)) {
			const linksToDelete = getLinksInvolvingNode(nodeToDelete, this.links);
			if (neuroCreate.saveLinks) {
				neuroCreate.saveLinks.delete(linksToDelete);
			}

			const groupsToDelete = getGroupsWhereNodeIsOnlyNode(nodeToDelete, this.nodeGroups);
			if (neuroCreate.saveGroups) {
				neuroCreate.saveGroups.delete(groupsToDelete);
			}

			if (neuroCreate.saveNodes) {
				neuroCreate.saveNodes.delete([nodeToDelete]);
			}

			// Whenever a node is removed from any board the Idea Box may show
			// (Inspire, personal Synthesise, team Synthesise), refresh the
			// hearted list so its "+" buttons recompute their disabled state
			// against the now-smaller node set. The Idea Box only renders
			// in those modes, so we scope this to avoid spurious re-renders
			// while users are working on Ideate.
			if (this.graphMode === 'inspire' || this.graphMode === 'moodboard') {
				refreshHeartedList();
			}
		} else {
			console.error(`Failed to find a node with uid ${nodeToDelete?.uid}`);
		}
	}

	/**
	 * Updates the position of the mouse
	 */
	onMouseMove = (event: MouseEvent) => {
		const { id: targetId } = event.target as SVGElement;
		const [x, y] = this.getMousePosition(event);
		this.mouse = { x, y, targetId };
		this.onMouseMoved();
	};

	onMouseLeave = () => {
		this.mouse = null;
		this.onMouseMoved();
	};

	/**
	 * Connect to another node
	 */
	connect = (node: NCNode) => {
		this.startSelectingNodes('connectFirstNode', [node]);

		showNonBlockingMessage('Select another node to connect to', [
			{
				label: 'Cancel',
				onClick: () => {
					this.stopSelectingNodes();
				},
			},
		]);

		tutorialEvents.clickedConnect(node);
	};

	mergeNodes(mergeFirstNode: NCNode, mergeSecondNode: NCNode): void {
		const mergedNode = this.addSingleNode(
			mergeFirstNode,
			`${mergeFirstNode.label || ''} ${mergeSecondNode.label || ''}`.trim(),
			undefined,
			[],
			[mergeSecondNode]
		);
		tutorialEvents.mergedNodes(mergedNode);
	}

	findNearestFreeSpace(proposedPoint: Point, linkedPoint: NCNode | undefined): Point {
		return findFreeSpaceRadial(this.nodes, this.links, proposedPoint, linkedPoint, this.nodeRadius);
	}

	/**
	 * Adds a new node below the current node.
	 */
	addSingleNode(
		node: NCNode,
		label: string,
		nodeColour?: EmpathiseColour | undefined,
		topics: string[] = [],
		additionalNodesToLink: Array<NCNode> = [],
		src?: string,
		isVideo: boolean = false,
		href?: string,
		pdfMetadata?: Pick<
			TopicAnalysis,
			'pdfInsightId' | 'pdfAnalysisId' | 'pdfFileName' | 'pdfProvenance'
		>
	): NCNode {
		const liveNode = (item: NCNode): NCNode => this.getNode(item.uid) || item;
		const liveSource = liveNode(node);
		const allNodesToLink = getNewNodeLinkTargets(
			liveSource,
			additionalNodesToLink.map(liveNode)
		).filter((item) => Boolean(this.getNode(item.uid)));
		const anchor = allNodesToLink[0] || liveSource;
		const hasLinkTargets = allNodesToLink.length > 0;
		let newNodeProposedPoint = {
			x: anchor.x + 100 * Math.random() - 50,
			y: anchor.y + 100,
		};
		if (allNodesToLink.length >= 2) {
			// Place new node at the mid-point between the linked nodes
			const avg = (arr: Array<number>) => arr.reduce((acc, v, i, a) => acc + v / a.length, 0);
			newNodeProposedPoint = {
				x: avg(allNodesToLink.map((n) => n.x)),
				y: avg(allNodesToLink.map((n) => n.y)),
			};
		}
		newNodeProposedPoint = this.findNearestFreeSpace(newNodeProposedPoint, anchor);
		const newNode = this.spawnNewNode(
			{
				...newNodeProposedPoint,
				label,
				src,
				nodeColour,
				topics,
				isVideo,
				href,
				...(pdfMetadata?.pdfAnalysisId ? { style: 'rect' as const } : {}),
				...pdfMetadata,
			},
			hasLinkTargets ? allNodesToLink : null,
			!hasLinkTargets
		);
		tutorialEvents.addedSingleNode(newNode);
		return newNode;
	}

	connectNodes(connectFirstNode: NCNode, connectSecondNode: NCNode): void {
		const newLink: NCLink = {
			source: connectFirstNode,
			target: connectSecondNode,
			colour: this.userColourClass,
		};
		if (!this.links.some((l) => isSameLink(l, newLink))) {
			if (neuroCreate.saveLinks) {
				neuroCreate.saveLinks.create([newLink]);
			}
		} else {
			showAlert('The nodes are already connected');
			clearNonBlockingMessage();
		}

		this.removeActiveStatus();
		this.activeLink = { source: connectFirstNode.uid, target: connectSecondNode.uid };
		this.appendMenuElements();
		clearNonBlockingMessage();

		setTimeout(() => {
			tutorialEvents.connectedNodes(connectSecondNode);
		}, 50);
	}

	addNodeToSelection(
		key: keyof typeof this.selections &
			(
				| 'commonIdeasNodes'
				| 'groupingNodes'
				| 'linkingNodes'
				| 'synthesiseNodes'
				| 'addToGroup'
				| 'removeFromGroup'
			),
		node: NCNode
	) {
		const selection = this.selections[key];
		if (selection) {
			const textNodesOnly = ['commonIdeasNodes', 'linkingNodes'].includes(key);
			const newNodesList = addToNodeList(node, selection, textNodesOnly);
			if (newNodesList) {
				this.selections[key] = newNodesList;
				return true;
			}
		}
		return false;
	}

	completeNodeSelection(node: NCNode) {
		tutorialEvents.selectedNode(node);

		if (this.selections.mergeFirstNode || this.selections.connectFirstNode) {
			if (this.selections.mergeFirstNode) {
				this.mergeNodes(this.selections.mergeFirstNode, node);
			} else if (this.selections.connectFirstNode) {
				this.connectNodes(this.selections.connectFirstNode, node);
			}

			this.stopSelectingNodes();
			return;
		}

		if (this.addNodeToSelection('commonIdeasNodes', node)) {
			showCommonIdeasNodesPrompt(this.selections.commonIdeasNodes!);
		} else if (this.addNodeToSelection('groupingNodes', node)) {
			showCreateGroupPrompt();
		} else if (this.addNodeToSelection('linkingNodes', node)) {
			showLinkingNodesPrompt(this.selections.linkingNodes!);
		} else if (this.addNodeToSelection('synthesiseNodes', node)) {
			showSynthesiseNodesPrompt(this.selections.synthesiseNodes!);
		} else if (this.addNodeToSelection('addToGroup', node)) {
			const node = this.selections.addToGroup![0];
			if (this.activeGroup?.nodes.some((n) => n.uid === node.uid)) {
				showAlert('This node is already in the group so cannot be added again');
				this.stopSelectingNodes();
			} else {
				if (this.activeGroup) {
					this.addNodesToGroup(this.activeGroup, [node]);
				}
				this.stopSelectingNodes();
			}
		} else if (this.addNodeToSelection('removeFromGroup', node)) {
			const node = this.selections.removeFromGroup![0];
			if (!this.activeGroup?.nodes.some((n) => n.uid === node.uid)) {
				showAlert('This node is not in the group so cannot be removed');
				this.stopSelectingNodes();
			} else {
				const newGroupNodes = this.activeGroup.nodes.filter((n) => n.uid !== node.uid);
				if (newGroupNodes.length < 2) {
					// Remove group if only one node left
					if (neuroCreate.saveGroups) {
						neuroCreate.saveGroups.delete([this.activeGroup]);
					}
					this.removeActiveStatus();
				} else {
					if (this.activeGroup) {
						this.removeNodesFromGroup(this.activeGroup, [node]);
					}
				}
				this.stopSelectingNodes();
			}
		} else {
			console.error('No action to complete node selection');
		}
	}

	/**
	 * Places a new node in the graph and updates this.data
	 */
	spawnNewNode(
		nodeDefinition: NCNodeDefinition,
		nodesToLink: null | Array<NCNode> = null,
		noLinking = false
	): NCNode {
		const newNode = applyDefaultGraphNodeShape(
			this.graphMode,
			constructNodeFromDefinition(nodeDefinition, this.userColourClass, this.currentUser.id)
		);
		const newLinks: Array<NCLink> = [];

		if (nodesToLink) {
			newLinks.push(
				...nodesToLink.map((nodeToLink) => ({
					source: newNode,
					target: nodeToLink,
					colour: this.userColourClass,
				}))
			);
			if (nodesToLink.length === 1) {
				newNode.parent = {
					uid: nodesToLink[0].uid,
					label: nodesToLink[0].label || '',
				};
			}
		} else if (!noLinking) {
			const closestNodeToLinkTo = findClosestNodeToLinkTo(newNode, this.nodes, this.nodeRadius);

			if (closestNodeToLinkTo) {
				newLinks.push({
					source: newNode,
					target: closestNodeToLinkTo,
					colour: this.userColourClass,
				});
			}
		}
		// Unlock all nodes before updating physics
		this.lockAllNodesInPlace(false);
		// updatePhysics(this);
		resetPhysicsMovement(this);

		// Save new node & links
		if (neuroCreate.saveNodes) {
			neuroCreate.saveNodes.create([newNode]);
		}
		if (neuroCreate.saveLinks) {
			neuroCreate.saveLinks.create(newLinks);
		}

		this.removeActiveStatus();
		return newNode;
	}

	startSelectingNodes(key: keyof typeof this.selections, nodes: Array<NCNode>, extraClasses = '') {
		for (const key of Object.keys(this.selections)) {
			this.selections[key as keyof typeof this.selections] = undefined;
		}

		if (key === 'mergeFirstNode' || key === 'connectFirstNode') {
			this.selections[key] = nodes.length > 0 ? nodes[0] : undefined;
		} else {
			if (this.activeNode) {
				this.updateActive(this.activeNode);
			}

			this.selections[key] = nodes;
		}

		this.baseElement.classed(`selecting ${extraClasses}`, true);
		this.onTick();
	}

	stopSelectingNodes(extraClasses = '') {
		this.baseElement.classed(`selecting ${extraClasses}`, false);

		for (const key of Object.keys(this.selections)) {
			this.selections[key as keyof typeof this.selections] = undefined;
		}
		clearNonBlockingMessage();
		tutorialEvents.doneSelectingItems();
	}

	/**
	 * onClick is called when the user clicks anywhere inside the svg.
	 * The event parameter holds information on the target that is clicked on.
	 * Using a switch case we write out the logic that is called on each specific target.
	 */
	onClick(event: MouseEvent) {
		const { targetClass, targetId, element } = getMouseTargetInfo(event);

		const isSelectingNode = this.baseElement.classed('selecting');

		if (
			isSelectingNode &&
			!['node', 'img-div', 'video-div', 'input', 'cluster-heart', 'cluster-heart-circle'].includes(
				targetClass
			)
		) {
			if (!this.suppressBlankCanvasSelectionAlert()) showAlert('Please select another node');
			return;
		}

		const completeSelection = () => {
			if (isSelectingNode) {
				const node = this.getNodeFromCircle(event);
				if (!node) {
					if (!this.suppressBlankCanvasSelectionAlert()) showAlert('Please select another node');
					return;
				}
				const sourceUid =
					this.selections.connectFirstNode?.uid ||
					this.selections.mergeFirstNode?.uid ||
					this.activeNode?.uid;
				if (!this.selections.groupingNodes && sourceUid && sourceUid === node.uid) {
					showAlert('Please select a different node');
				} else {
					this.completeNodeSelection(node);
				}
			}
		};

		switch (targetClass) {
			case 'link-delete-circle':
			case 'delete-link': {
				this.deleteLink(event);
				return;
			}
			case 'link-holder':
			case 'link-line': {
				if (this.isMovable()) {
					this.updateActiveLink(event);
				}
				return;
			}
			case 'cluster-heart':
			case 'cluster-heart-circle': {
				const clusterData = (element as unknown as ClusterBoundElement).__data__;
				(this as unknown as IdeateGraph).toggleLikeCluster(clusterData);
				return;
			}
			case 'delete': {
				const parent = getParent(event);
				const node = this.getNode(parent.datum().uid);
				if (node) {
					this.delete(node);
				}
				return;
			}
			case 'input': {
				this.updateActive(event, false);
				completeSelection();
				return;
			}
			case 'group': {
				const parent = getParent(event);
				this.activateGroup(this.nodeGroups.find((group) => group.uid === parent.datum().uid));
				return;
			}
			case 'img-div':
			case 'video-div':
			case 'node': {
				this.updateActive(event);
				completeSelection();
				return;
			}
			case 'menu-rect':
			case 'arc':
			case 'menu-button': {
				const menuButton = getRadialMenuButtonArc(event);
				if (menuButton.classed('disabled')) return;
				const action = menuButton.attr('data-action');
				const node = this.getNodeFromButton(event);

				switch (action) {
					case 'like':
						this.toggleLike(node);
						break;
					case 'spark':
						(this as unknown as InspireGraph).spark(node);
						break;
					case 'empathise':
						(this as unknown as InspireGraph).empathise(node);
						break;
					case 'generate':
						this.generate(node);
						break;
					case 'synthesise':
						(this as unknown as MoodboardGraph).synthesise(node);
						break;
					case 'search':
						(this as unknown as InspireGraph).search(node);
						break;
					case 'link':
						(this as unknown as InspireGraph).linker(node);
						break;
					case 'merge':
						(this as unknown as InspireGraph).merge(node);
						break;
					case 'delete':
						this.delete(node);
						break;
					case 'note':
						(this as unknown as MoodboardGraph).addToNote(node);
						break;
					case 'view':
						this.viewImage(node);
						break;
					case 'connect':
						this.connect(node);
						break;
					case 'share':
						(this as unknown as MoodboardGraph).share(node);
						break;
					case 'expand':
						void this.expand(node);
						break;
				}
				return;
			}
		}

		switch (targetId) {
			case 'graph-background':
				this.onMouseMove.call(this, event);
				if (this.isEditable() && this.mouse) {
					// if (this.activeNode) {
					// 	// First disable the active node
					// 	this.updateActive(this.activeNode);
					// } else
					if (
						!this.nodes.some(
							(n) =>
								this.mouse && getDistanceForConnection(this.mouse, n, this.nodeRadius).isTooClose
						)
					) {
						// Clear the message that the current node was created by another user
						clearNonBlockingMessageWithMessage('This node was added by');

						const newNode = this.spawnNewNode({ x: this.mouse.x, y: this.mouse.y });
						tutorialEvents.userCreatedNode(newNode);
					}
				}
				return;
		}
	}

	/** Analysis explorers use the blank canvas for navigation, not node selection. */
	protected suppressBlankCanvasSelectionAlert(): boolean {
		return false;
	}

	createNewEmptyNode(): NCNode | undefined {
		if (this.isEditable()) {
			const point = findFreeSpace(
				this.nodes,
				this.mouse || this.nodes.at(-1) || { x: 0, y: 0 },
				this.nodeRadius
			);
			const newNode = this.spawnNewNode({ x: point.x, y: point.y }, null, true);
			tutorialEvents.userCreatedNode(newNode);
			this.centerTo(newNode);
			return newNode;
		}
		return undefined;
	}

	updateNodesInGroup(group: NCGroup, nodes: Array<NCNode>) {
		group.nodes = nodes;
		this.activateGroup(group);
	}

	createGroup(nodes: Array<NCNode>): NCGroup {
		const group: NCGroup = {
			uid: generateNodeId(),
			nodes: nodes,
			colour: this.userColourClass,
			overrideColour: getRandomGroupColor(),
		};

		if (neuroCreate.saveGroups) {
			neuroCreate.saveGroups.create([group], () => {
				this.activateGroup(group);
			});
		}

		return group;
	}

	addNodesToGroup(group: NCGroup, nodes: Array<NCNode>) {
		const updatedGroup = {
			...group,
			nodes: group.nodes.concat(nodes),
		};
		if (neuroCreate.saveGroups) {
			neuroCreate.saveGroups.update([updatedGroup]);
		}
		return updatedGroup;
	}

	removeNodesFromGroup(group: NCGroup, nodes: Array<NCNode>) {
		const updatedGroup = {
			...group,
			nodes: group.nodes.filter((n) => !nodes.some((node) => node.uid === n.uid)),
		};
		if (neuroCreate.saveGroups) {
			neuroCreate.saveGroups.update([updatedGroup]);
		}
		return updatedGroup;
	}

	spawnCluster(
		cluster: NCCluster,
		clusterProps: {
			x: number;
			y: number;
			colourClass: AvailableColour;
			radius?: number;
			styleLongAsRect?: boolean;
			markExpandableText?: boolean;
			existingCentralNode?: NCNode;
			mutateLinkedNode?: (node: NCNode) => void;
			skipLinks?: boolean;
		},
		isRectangular?: boolean | undefined,
		linkedPhraseMeta?: TopicAnalysis[]
	): NCNode {
		console.log('[cluster]', cluster);

		const { centralNode, linkedNodes } = createClusterNodes(
			cluster,
			clusterProps,
			this.currentUser.id,
			this.graphMode === 'ideate' ? globalThis.neuroCreate.doc!.data.inspire.data : undefined,
			clusterProps.existingCentralNode
		);
		if (isRectangular) {
			centralNode.style = 'rect';
			centralNode.topics = ['source:vision'];
			linkedNodes.forEach((node, index) => {
				node.style = 'rect';
				const phraseMeta = linkedPhraseMeta?.[index];
				if (phraseMeta?.topics?.length) {
					node.topics = phraseMeta.topics;
				}
			});
		}
		if (clusterProps.styleLongAsRect || clusterProps.markExpandableText) {
			linkedNodes.forEach((node) => {
				const label = node.label || '';
				const isLong = isLongNodeLabel(label) || label.endsWith('…');
				if (!isLong && !clusterProps.markExpandableText) return;
				if (clusterProps.styleLongAsRect && isLong) {
					node.style = 'rect';
				}
				if (clusterProps.markExpandableText && (isLong || label.length > 24)) {
					node.topics = [...(node.topics || []), 'expandable-text'];
				}
			});
		}
		linkedNodes.forEach((node) => clusterProps.mutateLinkedNode?.(node));
		if (neuroCreate.saveNodes) {
			neuroCreate.saveNodes.create([
				...(clusterProps.existingCentralNode ? [] : [centralNode]),
				...linkedNodes,
			]);
		}
		if (neuroCreate.saveLinks && !clusterProps.skipLinks) {
			console.log(
				'[CreateLinks]',
				linkedNodes.map((n) => ({ source: centralNode, target: n, colour: this.userColourClass }))
			);

			neuroCreate.saveLinks.create(
				linkedNodes.map((n) => ({ source: centralNode, target: n, colour: this.userColourClass }))
			);
		}
		tutorialEvents.addedCluster(cluster, clusterProps);

		return centralNode;
	}

	get graphProps(): GraphProps {
		return {
			areNodesColoured: this.areNodesColoured(),
			areLinksColoured: this.areLinksColoured(),
			userColourClass: this.userColourClass,
			userColourHex: this.userColourHex,
			nodeRadius: this.nodeRadius,
			currentUser: this.currentUser,
			isEditable: this.isEditable(),
			menuOptions: this.getMenuOptions(),
			boardUsers: this.boardUsers,
			aiStatus: this.aiStatus,
			activeLink: this.activeLink,
			isDeleteAnimated: this.isMovable(),
			automation: this.automation,
			scale: this.zoomTransform?.k || 1,
			graphMode: this.graphMode,
			onToggleNodeLike: (node) => this.toggleLike(node),
		};
	}

	onMouseMoved() {
		const graphProps = this.graphProps;
		if (graphProps.isEditable) {
			updateCursorAndMouseLink(this.groups.cursor, this.mouse, this.activeNode);

			if (this.groups.debugMarker && this.debugMarker) {
				this.groups.debugMarker
					.attr('cx', (this.debugMarker && this.debugMarker.x) || 0)
					.attr('cy', (this.debugMarker && this.debugMarker.y) || 0);
			}

			this.groups.mouselink = updateMouseLink(
				this.groups.mouselink,
				this.mouse,
				graphProps,
				this.nodes
			);
		}
	}

	onTick() {
		if (!this.debouncedOnTick) {
			// Debounce onTick to avoid performance issues if multiple updates are called in quick succession
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call
			this.debouncedOnTick = debounce(
				() => {
					if (this.groups.nodes) {
						const selectedNodeIds = new Set(
							Object.values(this.selections)
								.filter((selectedNodes): selectedNodes is Array<NCNode> =>
									Array.isArray(selectedNodes)
								)
								.flatMap((selectedNodes) => selectedNodes.map((node) => node.uid))
						);
						const socialCardSelectionBackground =
							this.graphMode === 'ideate' ? '#ffff99' : 'var(--blue)';
						const socialCardSelectionText =
							this.graphMode === 'ideate' ? '#000' : '#fff';
						const isSelected = (node: NCNode): boolean => selectedNodeIds.has(node.uid);
						const isActive = (node: NCNode): boolean =>
							this.currentUser.activeNode === node.uid;
						const isHighlighted = (node: NCNode): boolean =>
							isSelected(node) || isActive(node);
						const highlightBackground = (node: NCNode): string =>
							isActive(node)
								? `var(--${this.currentUser.colour})`
								: socialCardSelectionBackground;
						const highlightText = (node: NCNode): string =>
							isActive(node) || this.graphMode !== 'ideate' ? '#fff' : socialCardSelectionText;

						// Ensure selected nodes are highlighted. Social evidence cards render their
						// visible surface in an HTML foreignObject, above the SVG node shape, so
						// they need the same state applied directly to that surface as well.
						this.groups.nodes.classed('selected', isHighlighted);

						// Preserve the image/video itself. The card's caption is the
						// comparable background surface to a text node, so it receives the
						// shared selected-state colour instead.
						const mediaCards = this.groups.nodes.select<HTMLElement>('.img-div, .video-div');
						mediaCards
							.style('background', (d) => (isHighlighted(d) ? highlightBackground(d) : '#fff'))
							.style('border-color', (d) =>
								isHighlighted(d) ? highlightBackground(d) : '#fff'
							)
							.style('box-shadow', 'none');
						mediaCards
							.select<HTMLElement>('.node-media-card__caption')
							.style('background', (d) => (isHighlighted(d) ? highlightBackground(d) : '#fff'))
							.style('color', (d) => (isHighlighted(d) ? highlightText(d) : '#302e40'));

						this.groups.nodes
							.select<HTMLElement>('.grounding-evidence-card')
							.style('background', (d) =>
								isHighlighted(d) ? highlightBackground(d) : '#fff'
							)
							.style('border-color', (d) =>
								isHighlighted(d) ? highlightBackground(d) : '#d9d7e6'
							)
							.style('color', (d) =>
								isHighlighted(d) ? highlightText(d) : '#302e40'
							)
							.selectAll<HTMLElement, NCNode>(
								'.grounding-evidence-card__source, .grounding-evidence-card__pill'
							)
							.style('color', '#302e40');
					}

					if (this.groups.nodes && this.groups.links && this.groups.groups) {
						updateNodePositions(this.groups.nodes);
						updateLinkPositions(this.groups.links);
						updateGroupPositions(this.groups.groups);
						if (this.simulation?.alpha && this.simulation.alpha() > 0.2) {
							tutorial.updatePosition();
						}
					}

					this.onMouseMoved();
				},
				2,
				{ maxWait: 20 }
			);
		}

		this.debouncedOnTick();
	}

	get nodeRadius(): number {
		return 48;
	}

	enterNode(
		elem: d3.Selection<d3.EnterElement, NCNode, SVGGElement, null>
	): d3.Selection<SVGGElement, NCNode, SVGGElement, null> {
		return enterNode(
			elem,
			this.graphProps,
			this.wrappedUpdateLabel.bind(this),
			this.nodeDragBehavior,
			(node) => this.activateOrSelectNode(node),
			(node) => this.openNodeContent(node)
		);
	}

	/**
	 * Card nodes (PDF documents, Ideate outputs) render their body as HTML and
	 * stop the click before it reaches the svg-level `onClick`, so they have to
	 * repeat the selection branch that plain node shapes get there.
	 */
	private activateOrSelectNode(node: NCNode): void {
		const isSelectingNode = this.isSelectingNode();
		this.updateActive(node);
		if (!isSelectingNode) {
			return;
		}
		const sourceUid =
			this.selections.connectFirstNode?.uid ||
			this.selections.mergeFirstNode?.uid ||
			this.activeNode?.uid;
		if (!this.selections.groupingNodes && sourceUid && sourceUid === node.uid) {
			showAlert('Please select a different node');
			return;
		}
		this.completeNodeSelection(node);
		this.onTick();
	}

	protected openNodeContent(node: NCNode): void {
		if (node.src) {
			this.viewMedia(node);
			return;
		}
		if (node.href && /^https?:\/\//i.test(node.href)) {
			this.openWebResultContent(node);
			return;
		}
		if (node.groundingEvidence?.source.startsWith('social-profile:')) {
			this.openSocialPostContent(node);
			return;
		}
		const isSynthesiseOutput = Boolean(node.synthesiseRecipe);
		const positioningOutput = node.positioningOutput
			? toReadablePositioningOutput(node.positioningOutput)
			: undefined;
		const modalContent = document.createElement('article');
		modalContent.className = `pdf-summary-modal${
			isSynthesiseOutput || positioningOutput ? ' pdf-summary-modal--synthesise' : ''
		}`;

		const eyebrow = document.createElement('p');
		eyebrow.className = 'pdf-summary-modal__eyebrow';
		eyebrow.textContent =
			positioningOutput?.eyebrow || (isSynthesiseOutput ? 'Synthesise output' : 'PDF document');

		const title = document.createElement('h2');
		title.className = 'pdf-summary-modal__title';
		title.textContent =
			positioningOutput?.title ||
			(isSynthesiseOutput
				? getSynthesiseOutputTitle(node.synthesiseRecipe, 'Generated output')
				: getPdfDisplayName(node));

		const summaryHeading = document.createElement('h3');
		summaryHeading.className = 'pdf-summary-modal__summary-heading';
		summaryHeading.textContent =
			isSynthesiseOutput || positioningOutput ? 'Output snapshot' : 'Summary';

		const summary = document.createElement('p');
		summary.className = 'pdf-summary-modal__summary';
		summary.textContent =
			positioningOutput?.summary ||
			node.label ||
			(isSynthesiseOutput
				? 'No generated content is available for this output.'
				: 'No summary is available for this document.');

		modalContent.append(eyebrow, title, summaryHeading, summary);
		positioningOutput?.details.forEach((detail) => {
			const heading = document.createElement('h3');
			heading.className = 'pdf-summary-modal__summary-heading';
			heading.textContent = detail.title;
			const value = document.createElement('p');
			value.className = 'pdf-summary-modal__summary';
			value.textContent = detail.value;
			modalContent.append(heading, value);
		});
		createDialog('', [modalContent], createButtons([{ label: 'Close', primary: true }], true));
	}

	private openWebResultContent(node: NCNode): void {
		const href = node.href;
		if (!href) return;
		const modalContent = document.createElement('article');
		modalContent.className = 'pdf-summary-modal';
		modalContent.style.width = 'min(1120px, calc(100vw - 64px))';
		modalContent.style.maxWidth = '100%';

		const eyebrow = document.createElement('p');
		eyebrow.className = 'pdf-summary-modal__eyebrow';
		eyebrow.textContent = 'Web result';
		const title = document.createElement('h2');
		title.className = 'pdf-summary-modal__title';
		title.textContent = node.label || 'Web page';
		const frame = document.createElement('iframe');
		frame.src = href;
		frame.title = `Web page: ${node.label || href}`;
		frame.loading = 'lazy';
		frame.referrerPolicy = 'no-referrer';
		frame.sandbox.add('allow-forms', 'allow-popups', 'allow-scripts', 'allow-same-origin');
		frame.style.display = 'block';
		frame.style.width = '100%';
		frame.style.height = 'min(70vh, 760px)';
		frame.style.marginTop = '18px';
		frame.style.border = '1px solid #dedbe6';
		frame.style.borderRadius = '10px';
		frame.style.background = '#fff';
		modalContent.append(eyebrow, title, frame);

		const note = document.createElement('p');
		note.textContent = 'Some publishers block embedded pages. If this page cannot be displayed here, open the source in a new tab.';
		note.style.margin = '10px 0 0';
		note.style.color = '#68647a';
		note.style.fontSize = '12px';
		modalContent.append(note);
		createDialog(
			'',
			[modalContent],
			createButtons(
				[
					{
						label: 'Open in new tab',
						onClick: () => {
							window.open(href, '_blank', 'noopener,noreferrer');
						},
					},
					{ label: 'Close', primary: true },
				],
				true
			)
		);
	}

	private openSocialPostContent(node: NCNode): void {
		const evidence = node.groundingEvidence;
		if (!evidence) return;
		const modalContent = document.createElement('article');
		modalContent.className = 'pdf-summary-modal pdf-summary-modal--grounding';

		const eyebrow = document.createElement('p');
		eyebrow.className = 'pdf-summary-modal__eyebrow';
		eyebrow.textContent = node.parent?.label || 'Social post';
		const title = document.createElement('h2');
		title.className = 'pdf-summary-modal__title';
		title.textContent = evidence.title || node.label || 'Social observation';
		modalContent.append(eyebrow, title);

		const socialMeta = document.createElement('div');
		socialMeta.className = 'grounding-social-meta';
		if (evidence.author) {
			const author = document.createElement('span');
			author.textContent = evidence.author;
			socialMeta.append(author);
		}
		if (typeof evidence.engagement?.likes === 'number') {
			const likes = document.createElement('span');
			likes.textContent = `${new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(evidence.engagement.likes)} like${evidence.engagement.likes === 1 ? '' : 's'}`;
			socialMeta.append(likes);
		}
		if (socialMeta.childElementCount) modalContent.append(socialMeta);

		if (evidence.image) {
			const media = document.createElement('figure');
			media.className = 'grounding-social-media';
			const image = document.createElement('img');
			image.src = evidence.image;
			image.alt = evidence.isVideo ? 'Video preview from the social post' : 'Image from the social post';
			image.loading = 'lazy';
			image.referrerPolicy = 'no-referrer';
			image.addEventListener('error', () => media.remove());
			media.append(image);
			if (evidence.isVideo) {
				const videoBadge = document.createElement('span');
				videoBadge.className = 'grounding-social-media__video';
				videoBadge.textContent = 'Video';
				media.append(videoBadge);
			}
			modalContent.append(media);
		}

		const heading = document.createElement('h3');
		heading.className = 'pdf-summary-modal__summary-heading';
		heading.textContent = 'Full social post';
		const content = document.createElement('p');
		content.className = 'pdf-summary-modal__summary';
		content.textContent = evidence.content || evidence.detail || evidence.title || node.label || 'No additional detail available.';
		modalContent.append(heading, content);
		if (evidence.url) {
			const sourceSection = document.createElement('section');
			sourceSection.className = 'pdf-summary-modal__source';
			const sourceHeading = document.createElement('h3');
			sourceHeading.className = 'pdf-summary-modal__summary-heading';
			sourceHeading.textContent = 'Source';
			const source = document.createElement('a');
			source.className = 'pdf-summary-modal__source-link';
			source.href = evidence.url;
			source.target = '_blank';
			source.rel = 'noopener noreferrer';
			source.textContent = evidence.url;
			sourceSection.append(sourceHeading, source);
			modalContent.append(sourceSection);
		}
		createDialog('', [modalContent], createButtons([{ label: 'Close', primary: true }], true));
	}

	/**
	 * Check that AI is enabled and node can be used with AI
	 */
	checkNodeReadyForAI(node: NCNode, toolName: string) {
		if (!this.aiStatus) {
			showAlert(`Please enable AI to use ${toolName}`);
			return false;
		}
		const { label } = node;
		if (!label) {
			showAlert(`Please enter a label to use ${toolName}`);
			return false;
		}

		return true;
	}

	/**
	 * Search tool
	 */
	search(node: NCNode) {
		const { label, src } = node;
		if (!label && !src) {
			showAlert(`Please enter a label to use Search`);
			return false;
		}

		if (node.label || node.src) {
			toolsUi.toggleToolContainer('search', node);
			searchUi.rememberSearchSourceNode(node);
			const runSearch = (
				searchMode: SearchMode = searchUi.getSearchModeForNode(node),
				lensId = 'all'
			) => {
				const requestId = searchUi.beginPanelSearchRequest(node, searchMode);
				const isCurrentRequest = () =>
					searchUi.isCurrentPanelSearchRequest(requestId, node, searchMode);
				searchUi.resetSearch();
				if (node.src && searchMode === 'visual_similarity') {
					ncApi
						.searchImage(this.boardId, node.src, searchMode)
						.then((result) => {
							if (!isCurrentRequest()) return;
							searchUi.showSearchResults(result.entries, node, {
								removedSensitive: result.removedSensitive,
								sensitivityExplanation: result.sensitivityExplanation,
								sensitiveFilteredCount: result.sensitiveFilteredCount,
								deferredGdelt: result.deferredGdelt,
								sourceDiagnostics: result.sourceDiagnostics,
							});
							tutorialEvents.clickedSearch(node);
							tutorialEvents.searchResultsGenerated(result.entries);
							this.setToolUsed('search');
						})
						.catch((error: unknown) => {
							if (!isCurrentRequest()) return;
							console.error('[searchImage] request failed', error);
							searchUi.showSearchResults([], node);
						});
				} else if (node.label) {
					ncApi
						.search(this.boardId, node.label, searchMode, undefined, lensId)
						.then((result) => {
							if (!isCurrentRequest()) return;
							searchUi.showSearchResults(result.entries, node, {
								removedSensitive: result.removedSensitive,
								sensitivityExplanation: result.sensitivityExplanation,
								sensitiveFilteredCount: result.sensitiveFilteredCount,
								deferredGdelt: result.deferredGdelt,
								sourceDiagnostics: result.sourceDiagnostics,
								availableLenses: result.availableLenses,
								selectedLensId: result.selectedLensId,
								onLensSearch: (nextLensId) => runSearch(searchMode, nextLensId),
							});
							tutorialEvents.clickedSearch(node);
							tutorialEvents.searchResultsGenerated(result.entries);
							this.setToolUsed('search');
							if (result.deferredGdelt) {
								void ncApi
									.search(this.boardId, node.label!, searchMode, 'gdelt', lensId)
									.then((lateResult) => {
										if (!isCurrentRequest()) return;
										searchUi.appendSearchResults(
											lateResult.entries,
											node,
											lateResult.sourceDiagnostics
										);
									})
									.catch((error: unknown) =>
										console.warn('[search][gdelt-deferred] request failed', error)
									);
							}
						})
						.catch((error: unknown) => {
							if (!isCurrentRequest()) return;
							console.error('[search] request failed', error);
							searchUi.showSearchResults([], node);
						});
				} else if (node.src) {
					ncApi
						.searchImage(this.boardId, node.src, searchMode)
						.then((result) => {
							if (!isCurrentRequest()) return;
							searchUi.showSearchResults(result.entries, node, {
								removedSensitive: result.removedSensitive,
								sensitivityExplanation: result.sensitivityExplanation,
								sensitiveFilteredCount: result.sensitiveFilteredCount,
							});
							tutorialEvents.clickedSearch(node);
							tutorialEvents.searchResultsGenerated(result.entries);
							this.setToolUsed('search');
						})
						.catch((error: unknown) => {
							if (!isCurrentRequest()) return;
							console.error('[searchImage] request failed', error);
							searchUi.showSearchResults([], node);
						});
				}
			};
			searchUi.renderSearchModeToggle(node, runSearch);
			runSearch();
		}
	}

	toggleLike(node: NCNode) {
		const isNowLiked = !isNodeLikedByUser(node, this.currentUser);
		const updatedNode = withNodeLikeState(node, this.currentUser, isNowLiked);
		node.likes = updatedNode.likes;
		// Keep every representation of this node's like action in sync without
		// re-rendering every card on a large board.
		const likedNodeSelection = this.groups.nodes?.filter((candidate) => candidate.uid === node.uid);
		if (likedNodeSelection && !likedNodeSelection.empty()) {
			updateNodeAndMenu(likedNodeSelection, this.graphProps);
		}

		if (neuroCreate.saveNodes) {
			neuroCreate.saveNodes.setLike(node.uid, this.currentUser, isNowLiked);
		}

		tutorialEvents.likedNode(node, isNowLiked);

		// When hearting a node on the Inspire board, mirror the heart to the
		// current user's personal Synthesise board: liking copies the node
		// (same uid, same position); un-liking removes the copy. The two
		// boards drift independently after the initial copy.
		if (this.graphMode === 'inspire' && neuroCreate.doc && neuroCreate.userFieldName) {
			if (isNowLiked) {
				const moodboardCopy: NCNode = {
					...structuredClone(node),
					likes: { ...(updatedNode.likes || {}) },
				};
				addNodeToPersonalMoodboard(neuroCreate.doc, neuroCreate.userFieldName, moodboardCopy);
			} else {
				removeNodeFromPersonalMoodboard(neuroCreate.doc, neuroCreate.userFieldName, node.uid);
			}
		}

		// When unhearting a node on the personal Synthesise board, mirror the
		// unlike onto the matching Inspire node (matched by uid). We update
		// the Inspire node directly via a ShareDB op rather than calling its
		// toggleLike, so the inspire→synthesise cascade is NOT triggered —
		// the Synthesise node and any other Synthesise state stay intact;
		// only the heart icon on Inspire flips off.
		if (this.graphMode === 'moodboard' && !this.isCollaborative && !isNowLiked && neuroCreate.doc) {
			setInspireNodeLike(neuroCreate.doc, node.uid, this.currentUser, false);
		}

		// Add to hearted list
		if (node.label || node.src) {
			ncApi
				.like(
					this.boardId,
					node.label
						? node.isVideo
							? 'video-inspire'
							: 'node'
						: node.isVideo
							? 'video-inspire'
							: 'image-inspire',
					node.label || null,
					node.src || null,
					!isNowLiked,
					undefined,
					node.topics,
					node.uid
				)
				.then(() => {
					refreshHeartedList();
				});
		}
	}

	/**
	 * Generate AI tool
	 */
	generate(node: NCNode) {
		if (!this.checkNodeReadyForAI(node, 'Generate')) {
			return;
		}

		toolsUi.toggleToolContainer('generate', node);
		let generateTask: GenerateTask = 'persona';
		if (this.graphMode === 'inspire') {
			generateUi.showBrainstormTab(node);
			this.setToolUsed('generate');
			return;
		} else if (this.graphMode === 'ideate') {
			// Default to the first generate tool that's actually allowed for the
			// active analysis. Some analyses (Motivations, Motivation-*) don't
			// support persona generation, so falling back to it would launch a
			// no-op tool. ANALYSES_TYPES is the source of truth.
			const ideateGraph = this as unknown as IdeateGraph;
			const activeAnalysis = ideateGraph.activeAnalysis;
			if (activeAnalysis) {
				const allowed = ANALYSES_TYPES[activeAnalysis]?.generate || [];
				if (allowed.length && !allowed.includes(generateTask)) {
					generateTask = allowed[0] as GenerateTask;
				}
			}
		}
		generateUi.fetchGenerateResults([node], generateTask).then(() => {
			tutorialEvents.clickedGenerate(node);
			this.setToolUsed('generate');
		});
	}

	setToolUsed(tool: 'spark' | 'empathise' | 'generate' | 'search' | 'link' | 'explore'): void {
		this.currentUser.settings.used = this.currentUser.settings.used || {};

		const valueBefore = this.currentUser.settings.used[tool];
		this.currentUser.settings.used[tool] = true;

		if (valueBefore !== this.currentUser.settings.used[tool]) {
			this.saveUser();
		}
	}

	removeGroup(group: NCGroup, skipConfirm = false) {
		if (skipConfirm) {
			if (neuroCreate.saveGroups) {
				neuroCreate.saveGroups.delete([group]);
			}
		} else {
			confirmRemoveGroup(() => {
				if (neuroCreate.saveGroups) {
					neuroCreate.saveGroups.delete([group], () => {
						this.removeActiveStatus();
					});
				}
			});
		}
	}

	deleteLink(event: Event) {
		const linkToDelete = (event.target as LinkBoundElement).__data__;
		if (neuroCreate.saveLinks) {
			neuroCreate.saveLinks.delete([linkToDelete]);
		}
	}

	removeLink(linkToDelete: NCLink) {
		if (neuroCreate.saveLinks) {
			neuroCreate.saveLinks.delete([linkToDelete]);
		}
	}

	removeActiveStatus() {
		if (this.activeNode && this.currentUser) {
			this.currentUser.activeNode = undefined;
			this.saveUser();
			this.appendMenuElements();
		}
		if (this.activeLink) {
			this.activeLink = undefined;
			updateActiveLink(this.groups.links!, this.graphProps);
		}
		if (neuroCreate.saveGroups) {
			neuroCreate.saveGroups.update(
				this.nodeGroups
					.filter((g) => g.active)
					.map((g) => {
						const updatedGroup = { ...g };
						delete updatedGroup.active;
						return updatedGroup;
					})
			);
		}
		toggleGroupEditControls(this);
	}

	getGroupForNode(node: NCNode) {
		return this.nodeGroups.find((g) => g.nodes.some((n) => n.uid === node.uid));
	}

	activateGroup(group: NCGroup | undefined) {
		const updatedGroups: Array<NCGroup> = [];

		for (const g of this.nodeGroups) {
			if (group?.uid === g.uid) {
				if (!g.active) {
					updatedGroups.push({
						...g,
						active: true,
					});

					if (neuroCreate.saveNodes) {
						// Unfix all nodes within the area of the group
						neuroCreate.saveNodes.update(
							getNodesWithinGroupArea(group, this.nodes)
								.filter((n) => n.fx)
								.map((n) => {
									const updatedNode = { ...n };
									delete updatedNode.fx;
									delete updatedNode.fy;
									return updatedNode;
								})
						);
					}
				}
			} else {
				if (g.active) {
					const updatedGroup = { ...g };
					delete updatedGroup.active;
					updatedGroups.push(updatedGroup);
				}
			}
		}

		if (neuroCreate.saveGroups) {
			neuroCreate.saveGroups.update(updatedGroups);
		}

		if (group) {
			const activeNode = this.activeNode;
			if (activeNode) {
				if (!group?.nodes.some((n) => n.uid === activeNode.uid)) {
					// Disable active node if not in this group
					this.updateActive(activeNode);
				}
			}
		}

		toggleGroupEditControls(this);
	}

	updateActiveLink(event: Event) {
		this.removeActiveStatus();

		const {
			source: { uid: source },
			target: { uid: target },
		} = (event.target as LinkBoundElement).__data__;
		if (this.activeLink && this.activeLink.source === source && this.activeLink.target === target) {
			this.activeLink = undefined;
		} else {
			this.activeLink = { source, target };
		}
		updateActiveLink(this.groups.links!, this.graphProps);
	}

	/**
	 * Specifies which of all nodes is active (can only be one at a time)
	 */
	updateActive(
		eventOrNode: MouseEvent | NCNode,
		showMenu = true,
		onlyIfNotActive = false,
		center = true,
		fillColor = true
	) {
		const isSelectingNode = this.baseElement.classed('selecting');
		// Keep the Cancel affordance while connect/merge selection is active.
		if (!isSelectingNode) {
			clearNonBlockingMessage();
		}
		console.log(new Date().toISOString(), '[updateActive] eventOrNode', eventOrNode);
		const isUserInitiated = isUserInitiatedNodeActivation(eventOrNode);
		if (isSelectingNode) {
			this.onTick();
			return; // Do nothing
		}

		this.activeLink = undefined;
		updateActiveLink(this.groups.links!, this.graphProps);

		let node: NCNode | undefined = undefined;
		if ((eventOrNode as NCNode).uid) {
			node = eventOrNode as NCNode;
		} else {
			const uid = ((eventOrNode as MouseEvent).target as NodeBoundElement)?.__data__.uid;
			if (uid) {
				node = this.getNode(uid);
			}
		}
		if (!node) {
			return;
		}

		if (onlyIfNotActive) {
			if (isSameNode(this.activeNode, node)) {
				return; // Do nothing
			}
		}
		const wasActive = this.currentUser.activeNode === node.uid;
		const isActivatingNow = (showMenu && !wasActive) || fillColor;

		if (isActivatingNow) {
			this.activateGroup(this.getGroupForNode(node));
		}

		const valueBefore = this.currentUser.activeNode;
		this.currentUser.activeNode = isActivatingNow ? node.uid : undefined;

		if (valueBefore !== this.currentUser.activeNode) {
			this.saveUser();
		}

		if (node.dotted) {
			const updatedNode = { ...node };
			delete updatedNode.dotted;
			if (neuroCreate.saveNodes) {
				neuroCreate.saveNodes.update([updatedNode]);
			}
		}

		const activeNodeIds = new Set([
			...this.groups.nodes!.filter('.menu-open').data().map((candidate) => candidate.uid),
			...[valueBefore, this.currentUser.activeNode].filter(
				(uid): uid is string => Boolean(uid)
			),
		]);
		this.appendMenuElements(
			this.groups.nodes!.filter((candidate) => activeNodeIds.has(candidate.uid))
		);
		this.groups.nodes!
			.filter((candidate) => candidate.uid === this.currentUser.activeNode)
			.raise();

		if (isActivatingNow && showMenu) {
			tutorialEvents.openedNodeMenu(node);
			if (this.graphMode === 'inspire') {
				tutorialEvents.selectedNode(node);
			}
		}
		if (isActivatingNow && isUserInitiated && this.graphMode === 'ideate') {
			const ideateGraph = this as unknown as IdeateGraph;
			// The cluster label sits on the node's parent (for child nodes) or
			// on the node itself when it is the cluster center.
			const clusterLabel =
				node.parent?.label || (node.label && !node.parent ? node.label : null) || null;
			tutorialEvents.clickedClusterNode(node, {
				clusterLabel,
				analysisKey: ideateGraph.activeAnalysis || null,
			});
		}
		if (isActivatingNow && !this.isActiveNodeOwnedByCurrentUser() && this.graphMode !== 'ideate') {
			const nodeOwner = getUserByColour(node.colour);

			showNonBlockingMessage(
				`This node was added by ${
					nodeOwner ? `<strong>${nodeOwner.username}</strong>` : 'someone else'
				}. You cannot edit, delete, or merge this node.`
			);
		}
		this.onTick();

		if (center) {
			this.centerTo(node);
		}
	}

	syncActiveNodeForDrag(node: NCNode | undefined) {
		// Only update the activeNode property without triggering a full save
		const valueBefore = this.currentUser.activeNode;
		this.currentUser.activeNode = node?.uid;
		if (valueBefore !== this.currentUser.activeNode) {
			// Use saveSingleUser to only update the activeNode property
			if (neuroCreate.saveSingleUser) {
				const userUpdate = {
					...this.currentUser,
					activeNode: this.currentUser.activeNode,
				};
				neuroCreate.saveSingleUser(userUpdate);
			}
		}
	}

	/**
	 * Takes the ID of a button and returns the matching node
	 */
	getNodeFromButton(event: MouseEvent): NCNode {
		const target = event.target as SVGElement;
		const arc = target.classList.contains('arc')
			? (target as SVGGElement)
			: ((target as SVGPathElement).parentNode as SVGGElement);
		const menu = arc.parentNode as SVGGElement;
		// Menus are explicitly rebound to their parent node datum on every graph
		// update. Use that stable source first: media nodes contain several HTML
		// foreignObjects, so the legacy "first div" lookup can resolve the wrong
		// element (or no id at all) and leave Synthesise open with blank context.
		const menuNode = d3.select<SVGGElement, NCNode>(menu).datum();
		if (menuNode?.uid) {
			const node = this.getNode(menuNode.uid);
			if (node) return node;
		}
		const nodeEl = menu.parentNode as SVGGElement;
		const id = d3
			.select(nodeEl)
			.select<HTMLElement>('foreignObject > a > .input, foreignObject > div')
			.attr('id');
		return this.getNode(id)!;
	}

	/**
	 * Takes a click event on a node shape / media card and returns the matching node
	 */
	getNodeFromCircle(event: MouseEvent): NCNode | undefined {
		const uid = resolveNodeUidFromEventTarget(event.target);
		return uid ? this.getNode(uid) : undefined;
	}

	getMenuOptions(): Array<MenuOption> {
		return [{ label: 'Like', enabled: true, longText: true, shortText: true }];
	}

	getCurrentUserNodes(): Array<NCNode> {
		return this.nodes.filter(
			(n) => n.colour === this.userColourClass || n.colour === this.userColourHex
		);
	}

	/**
	 * Split up all nodes for user, or a single node
	 *
	 * Return true if nodes were split up
	 */
	async splitNodes(
		node: undefined | NCNode = undefined,
		force = false,
		splitKeyPhrases = false
	): Promise<boolean> {
		const nodesIdsToSplit = node
			? [node.uid]
			: this.getCurrentUserNodes()
					.filter((n) => n.style !== 'rect')
					.map((n) => n.uid);

		const { nodesToUpdate, nodesToDelete, nodesToAdd, linksToAdd, linksToDelete } =
			await splitNodes(
				nodesIdsToSplit,
				this.nodes,
				this.links,
				this.userColourClass,
				force,
				splitKeyPhrases
			);
		if (neuroCreate.saveNodes) {
			neuroCreate.saveNodes.update(nodesToUpdate);
			neuroCreate.saveNodes.delete(nodesToDelete, () => {
				neuroCreate.saveNodes?.create(nodesToAdd, () => {
					if (neuroCreate.saveLinks) {
						neuroCreate.saveLinks.create(linksToAdd, () => {
							neuroCreate.saveLinks?.delete(linksToDelete);
						});
					}
				});
			});
		}

		const haveNodesChanged =
			nodesToDelete.length > 0 || nodesToAdd.length > 0 || nodesToUpdate.length > 0;
		if (haveNodesChanged && this.activeNode) {
			// Deactivate active node (if any)
			this.updateActive(this.activeNode);
		}

		return haveNodesChanged;
	}

	preservePhraseNode(node: NCNode) {
		if (node.style === 'rect') {
			this.appendMenuElements();
			return;
		}
		node.style = 'rect';
		if (neuroCreate.saveNodes) {
			neuroCreate.saveNodes.update([
				{
					...node,
					style: 'rect',
				},
			]);
		}
		this.appendMenuElements();
	}

	preserveAndExpandPhraseNode(node: NCNode, words: Array<string>) {
		this.preservePhraseNode(node);

		words.forEach((word) => {
			this.addSingleNode(node, word, undefined);
		});
		this.updateActive(node, true, false, false);
	}

	toggleAi() {
		this.aiStatus = !this.aiStatus;
		console.log(`AI is now ${this.aiStatus ? 'on' : 'off'}`);
		this.appendMenuElements();
		ncApi.toggleAi(this.boardId, this.aiStatus);
	}

	isAiEnabled() {
		return this.aiStatus;
	}

	saveSuggestions(tool: 'spark' | 'empathise', suggestions: Array<TopicAnalysis>) {
		const selections = suggestions.slice(0, 3);
		this.userSelections.push({
			tool,
			words: selections.map((item) => item.key),
			topicMapping: selections,
			timestamp: new Date().getTime(),
		});

		if (globalThis.neuroCreate.saveUserAnalytics) {
			globalThis.neuroCreate.saveUserAnalytics(this.userSelections);
		}
	}

	rebuildLinks() {
		// Ensure each link refers to a current node object
		for (const l of this.links) {
			const source = this.nodes.find((n) => n.uid === l.source.uid);
			if (source) {
				l.source = source;
			}
			const target = this.nodes.find((n) => n.uid === l.target.uid);
			if (target) {
				l.target = target;
			}
		}
	}

	/**
	 * @deprecated
	 * Generally this should not be used as it will update the entire graph rather than the specific parts that updated.
	 **/
	save(callback?: () => void) {
		this.rebuildLinks();

		if (globalThis.neuroCreate.saveGraph) {
			globalThis.neuroCreate.saveGraph(
				this.graphMode,
				{
					nodes: this.nodes,
					links: minimizeLinks(this.links),
					groups: minimizeGroups(this.nodeGroups),
				},
				callback
			);
		}
	}

	saveUser() {
		const saveSingleUser = globalThis.neuroCreate.saveSingleUser;
		if (saveSingleUser) {
			this.currentUser.lastSeen = new Date().getTime();
			this.currentUser.isOnline = true;

			const oneMinuteAgo = new Date(Date.now() - 60_000);
			this.boardUsers.forEach((boardUser) => {
				const newOnlineStatus = Boolean(
					boardUser.lastSeen && oneMinuteAgo.getTime() < new Date(boardUser.lastSeen).getTime()
				);
				if (newOnlineStatus !== boardUser.isOnline) {
					boardUser.isOnline = newOnlineStatus;
					saveSingleUser(boardUser);
				}
			});

			saveSingleUser(this.currentUser);
		}
	}

	appendMenuElements(
		nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null> = this.groups.nodes!
	) {
		updateNodeAndMenu(nodes, this.graphProps);
		tutorial.updatePosition();
	}

	updateNodeLabel(node: NCNode, newLabel: string) {
		// Update the node label in the graph locally to avoid local graph updates from sharedb
		const localNode = globalThis.neuroCreate.graph!.nodes.find((n) => n.uid === node.uid);
		if (localNode) {
			localNode.label = newLabel;
			if (node.style === 'rect') {
				localNode.style = 'rect';
			} else {
				delete localNode.style;
			}
		}
		if (neuroCreate.saveNodes) {
			neuroCreate.saveNodes.update([
				{
					...node,
					label: newLabel,
					...(node.style === 'rect' ? { style: 'rect' as const } : {}),
				},
			]);
		}
	}

	private syncNodeShapeToLabel(node: NCNode, label: string): boolean {
		if (!graphUsesRoundedRectNodes(this.graphMode)) return false;
		if (node.src || node.isVideo || node.pdfAnalysisId) return false;
		const wasRect = node.style === 'rect';
		if (isLongNodeLabel(label)) {
			if (wasRect) return false;
			node.style = 'rect';
			return true;
		}
		if (!label.trim() || !isLongNodeLabel(label)) {
			if (!wasRect) return false;
			delete node.style;
			return true;
		}
		return false;
	}

	wrappedUpdateLabel(event: InputEvent, finishedEditing = false): void {
		this.updateLabel(event, finishedEditing);
		const committed = finishedEditing || event.inputType === 'insertLineBreak';
		this.pendingInputEvent = committed ? null : event;
	}

	/**
	 * Updates the label of the input field inside the node
	 */
	updateLabel(event: InputEvent, finishedEditing = false): void {
		const targetEl = event.target as HTMLTextAreaElement;
		const node = this.getNode(targetEl.id)!;

		// Check if user pressed Enter by looking at inputType
		const isReallyFinishedEditing = finishedEditing || event.inputType === 'insertLineBreak';
		// Enter commits the label. The textarea still inserts a line break first,
		// so strip that trailing newline instead of saving it on the node.
		const newLabelValue = isReallyFinishedEditing
			? targetEl.value.replace(/\r?\n+$/, '')
			: targetEl.value;
		const shapeChanged = this.syncNodeShapeToLabel(node, newLabelValue);
		this.updateNodeLabel(node, newLabelValue);
		if (shapeChanged) {
			this.appendMenuElements();
		}

		if (isReallyFinishedEditing) {
			console.log('[updateLabel] isReallyFinishedEditing', isReallyFinishedEditing);
			targetEl.value = newLabelValue;
			targetEl.blur();
			// Make sure that the new node label is set
			node.label = newLabelValue;

			const words = splitWords(newLabelValue, false);
			if (this.aiStatus && finishedEditing && node.style !== 'rect') {
				if (isLongNodeLabel(newLabelValue)) {
					this.promptPreserveOrExpand(node);
				} else if (words.length === 1 && this.graphMode === 'inspire') {
					this.splitNodes(node);
				}
			} else if (!this.aiStatus && isLongNodeLabel(newLabelValue)) {
				this.preservePhraseNode(node);
			}

			tutorialEvents.updatedLabel(node);
		}
	}

	async promptExplore(node: NCNode) {
		if (!node.label) {
			return;
		}

		// const wordsOrTopics = node.label.length > 300 ? 'topics' : 'words';
		const wordsOrTopics: string = 'topics';

		// const keyWords =
		// 	wordsOrTopics === 'words' ? await splitTextIntoWords(this.boardId, node.label) : [];

		const keyWords: string[] = [];

		const explainerP = document.createElement('p');
		explainerP.classList.add('minimal-message');
		if (wordsOrTopics === 'words') {
			explainerP.innerHTML = `<strong>Expand</strong> will only add the key words ${keyWords.map((w) => `"${w}"`).join(', ')} in separate nodes to the board.<br/>
<strong>Preserve and Expand</strong> will keep and display both the full phrase and add the key words in separate nodes.<br/>`;
		} else {
			explainerP.innerHTML = `<strong>Expand</strong> will only add the key topics in separate nodes to the board.<br/>
<strong>Preserve and Expand</strong> will keep and display both the full phrase and add the key topics in separate nodes.<br/>`;
		}

		const sparkText = `The <strong>Spark</strong> AI tool suggests 2 ideas based on the concept. These will be associations within the domains related to the engine you selected. E.g., the 'Creative' engine gives associations within culture, lifestyle and trends.<br/>`;
		const empathiseText = `The <strong>Empathise</strong> AI tool suggests different perspectives related to your concept.<br>Suggestions are labelled by colour: <br><span class="colour-demo GREEN">Bright Green - positive</span><br> <span class="colour-demo RED">Red - negative</span><br> <span class="colour-demo FUSCHIA">Pink - emotions</span><br> <span class="colour-demo BLUE">Blue - co-occurence</span><br><br>`;

		explainerP.innerHTML = explainerP.innerHTML + sparkText + empathiseText;
		createDialog(
			'How would you like to explore this phrase using the AI?',
			[explainerP],
			createButtons(
				[
					{
						label: 'Expand',
						onClick: () => {
							const nodeCurrent = this.getNode(node.uid);
							if (nodeCurrent) {
								this.splitNodes(nodeCurrent, true, true);
							}
						},
					},
					{
						label: 'Preserve & Expand',
						primary: true,
						onClick: async () => {
							const nodeCurrent = this.getNode(node.uid);
							if (nodeCurrent) {
								if (wordsOrTopics === 'words') {
									this.preserveAndExpandPhraseNode(nodeCurrent, keyWords);
								} else {
									// Fetch topics from the API
									const keyTopics = await splitTextIntoWords(this.boardId, node.label!, 'topics');
									this.preserveAndExpandPhraseNode(nodeCurrent, keyTopics);
								}
							}
						},
					},
					{
						label: 'Spark',
						primary: true,
						onClick: async () => {
							const nodeCurrent = this.getNode(node.uid);
							if (nodeCurrent) {
								this.spark(nodeCurrent);
							}
						},
					},
					{
						label: 'Empathise',
						primary: true,
						onClick: async () => {
							const nodeCurrent = this.getNode(node.uid);
							if (nodeCurrent) {
								this.empathise(nodeCurrent);
							}
						},
					},
				],
				true
			)
		);
	}

	async promptPreserveOrExpand(node: NCNode) {
		if (!node.label) {
			return;
		}

		const wordsOrTopics = node.label.length > 300 ? 'phrases' : 'words';

		const keyWords =
			wordsOrTopics === 'words' ? await splitTextIntoWords(this.boardId, node.label) : [];

		// Get the currently focused input element (e.g. a new node label input of class 'input node-input')
		const focusedInputElement = document.activeElement as HTMLTextAreaElement;
		// console.log('[on promptPreserveOrExpand]', focusedInput);

		const explainerP = document.createElement('p');
		explainerP.classList.add('minimal-message');
		if (wordsOrTopics === 'words') {
			explainerP.innerHTML = `<small><strong>Preserve</strong> will keep and display the full phrase ${node.label.length > 100 ? '' : `"${node.label}"`} on the board.<br/>
<strong>Expand</strong> will only add the key words ${keyWords.map((w) => `"${w}"`).join(', ')} in separate nodes to the board.<br/>
<strong>Preserve and Expand</strong> will keep and display both the full phrase and add the key words in separate nodes.<br/><br/></small>`;
		} else {
			explainerP.innerHTML = `<small><strong>Preserve</strong> will keep and display the full phrase ${node.label.length > 100 ? '' : `"${node.label}"`} on the board.<br/>
<strong>Expand</strong> will only add the key topics in separate nodes to the board.<br/>
<strong>Preserve and Expand</strong> will keep and display both the full phrase and add the key topics in separate nodes.<br/><br/></small>`;
		}

		createDialog(
			'Would you like to preserve this phrase and/or<br> allow it to be expanded by AI?',
			[explainerP],
			createButtons(
				[
					{
						label: 'Preserve',
						onClick: () => {
							// Get latest node with the ID in case it has changed (e.g. moved)
							const nodeCurrent = this.getNode(node.uid);
							if (nodeCurrent) {
								this.preservePhraseNode(nodeCurrent);
								this.updateActive(nodeCurrent, true, false, false);
							}
							if (focusedInputElement) {
								// Focus the input element again
								focusedInputElement.focus();
							}
						},
					},
					{
						label: 'Expand',
						onClick: () => {
							const nodeCurrent = this.getNode(node.uid);
							if (nodeCurrent) {
								this.splitNodes(nodeCurrent, true, true);
							}
							if (focusedInputElement) {
								// Focus the input element again
								focusedInputElement.focus();
							}
						},
					},
					{
						label: 'Preserve & Expand',
						primary: true,
						onClick: async () => {
							const nodeCurrent = this.getNode(node.uid);
							if (nodeCurrent) {
								if (wordsOrTopics === 'words') {
									this.preserveAndExpandPhraseNode(nodeCurrent, keyWords);
								} else {
									// Fetch topics from the API
									const keyTopics = await splitTextIntoWords(this.boardId, node.label!, 'topics');
									this.preserveAndExpandPhraseNode(nodeCurrent, keyTopics);
								}
							}
							if (focusedInputElement) {
								// Focus the input element again
								focusedInputElement.focus();
							}
						},
					},
				],
				true
			)
		);
	}

	fetchEmpathiseResults(node: NCNode): Promise<void> {
		const filterSelect = document.querySelector<HTMLSelectElement>('#empathise-container .filter')!;
		let filterValue = '' as FilterOptions;
		if (filterSelect) {
			filterValue = filterSelect.value as FilterOptions;
		}
		return new Promise((resolve) => {
			if (node.label) {
				ncApi.empathise(this.boardId, filterValue, node.label).then((allResults) => {
					this.saveSuggestions(
						'empathise',
						allResults.result.map((a) => a.values[0] || '').filter((s) => s)
					);
					empathiseUi.setupEmpathiseResults(
						this.boardId,
						node.label!,
						(phrase, colour) =>
							this.addSingleNode(
								node,
								phrase.key,
								colour,
								phrase.topics,
								[],
								undefined,
								false,
								undefined,
								phrase
							),
						allResults.result,
						allResults.isFromTopic,
						buildNodeContextPath(this.nodes, node)
					);
					resolve();
				});
			} else {
				resolve();
			}
		});
	}

	/**
	 * Adds one Spark phrase as a child node.
	 */
	addSparkPhrase(parentNode: NCNode, auto: boolean, phrase: TopicAnalysis) {
		const nodeToLink = parentNode;
		const newNodeProposedPoint = this.findNearestFreeSpace(
			{
				x: nodeToLink.x - 30,
				y: nodeToLink.y + 120,
			},
			nodeToLink
		);
		const created = this.spawnNewNode(
			{
				...newNodeProposedPoint,
				dotted: auto,
				label: phrase.key,
				topics: phrase.topics,
				pdfInsightId: phrase.pdfInsightId,
				pdfAnalysisId: phrase.pdfAnalysisId,
				pdfProvenance: phrase.pdfProvenance,
			},
			[nodeToLink]
		);
		tutorialEvents.addedSingleNode(created);
	}

	/**
	 * Adds two new nodes below the current node.
	 */
	addTwoNodes(node: NCNode, auto = false, wordPair: [TopicAnalysis, TopicAnalysis]) {
		const nodeToLink = auto ? node : this.activeNode || node;
		const newNodeProposedPoint1 = this.findNearestFreeSpace(
			{
				x: nodeToLink.x - 60,
				y: nodeToLink.y + 120,
			},
			nodeToLink
		);
		const firstNode = this.spawnNewNode(
			{
				...newNodeProposedPoint1,
				dotted: auto,
				label: wordPair[0].key,
				topics: wordPair[0].topics,
				pdfInsightId: wordPair[0].pdfInsightId,
				pdfAnalysisId: wordPair[0].pdfAnalysisId,
				pdfProvenance: wordPair[0].pdfProvenance,
			},
			[nodeToLink]
		);

		const newNodeProposedPoint2 = this.findNearestFreeSpace(
			{
				x: newNodeProposedPoint1.x + 120,
				y: newNodeProposedPoint1.y,
			},
			nodeToLink
		);
		const secondNode = this.spawnNewNode(
			{
				...newNodeProposedPoint2,
				dotted: auto,
				label: wordPair[1].key,
				topics: wordPair[1].topics,
				pdfInsightId: wordPair[1].pdfInsightId,
				pdfAnalysisId: wordPair[1].pdfAnalysisId,
				pdfProvenance: wordPair[1].pdfProvenance,
			},
			[nodeToLink]
		);

		tutorialEvents.addedTwoNodes(firstNode, secondNode);
	}

	/**
	 * Empathise AI tool
	 */
	empathise(node: NCNode) {
		if (!this.checkNodeReadyForAI(node, 'Empathise')) {
			return;
		}

		toolsUi.toggleToolContainer('empathise', node);
		empathiseUi.resetEmpathise(this.fetchEmpathiseResults.bind(this, node));
		tutorialEvents.clickedEmpathise(node);
		this.setToolUsed('empathise');

		this.fetchEmpathiseResults(node);
	}

	/**
	 * Spark AI tool
	 */
	spark(node: NCNode, auto = false): Promise<void> {
		if (!this.checkNodeReadyForAI(node, 'Spark')) {
			return Promise.resolve();
		}

		return new Promise((resolve) => {
			toolsUi.toggleToolContainer('spark', node);
			if (node.label) {
				sparkUi.rememberSparkSourceNode(node);
				sparkUi.resetSpark();
				tutorialEvents.clickedSpark(node);
				this.setToolUsed('spark');

				ncApi.spark(this.boardId, node.label).then(async (suggestions) => {
					await sparkUi.setupSparkResults(
						this.boardId,
						node.label!,
						suggestions.result,
						suggestions.isFromTopic,
						this.addSparkPhrase.bind(this, node, auto),
						buildNodeContextPath(this.nodes, node)
					);
					this.saveSuggestions('spark', suggestions.result);
					resolve();
				});
			} else {
				resolve();
			}
		});
	}

	/**
	 * Adds one dotted Spark suggestion without opening the Spark panel. This is
	 * used while a new board is being seeded: it keeps the first board state
	 * legible and avoids presenting a tool interaction the user did not start.
	 */
	async addBootstrapSparkSuggestion(node: NCNode): Promise<boolean> {
		if (!this.checkNodeReadyForAI(node, 'Spark') || !node.label) {
			return false;
		}

		const suggestions = await ncApi.spark(this.boardId, node.label);
		const phrase = suggestions.result.find((candidate) => candidate.key?.trim());
		if (!phrase) {
			return false;
		}

		this.addSparkPhrase(node, true, phrase);
		this.saveSuggestions('spark', suggestions.result);
		return true;
	}

	/**
	 * Finds a node by its ID
	 */
	getNode(id: string): NCNode | undefined {
		return this.nodes.find((node) => node.uid === id);
	}

	private areNodesVisible(nodes: Array<NCNode>) {
		if (!nodes.length) {
			return true;
		}

		const svgBounds = this.svg.node()?.getBoundingClientRect();
		if (!svgBounds) {
			return true;
		}

		const currentTransform = d3.zoomTransform(this.baseSvgGroup);
		const margin = 110;

		return nodes.every((node) => {
			const screenX = svgBounds.width / 2 + currentTransform.applyX(node.x);
			const screenY = svgBounds.height / 2 + currentTransform.applyY(node.y);
			return (
				screenX >= margin &&
				screenX <= svgBounds.width - margin &&
				screenY >= margin &&
				screenY <= svgBounds.height - margin
			);
		});
	}

	focusNodesIfNeeded(nodeIds: string[], alwaysCenter = false) {
		const nodes = nodeIds
			.map((nodeId) => this.getNode(nodeId))
			.filter((node): node is NCNode => Boolean(node));
		if (!nodes.length) {
			return;
		}

		const primaryNode = nodes[0];
		this.updateActive(primaryNode, false, false, false, true);

		if (alwaysCenter || !this.areNodesVisible(nodes)) {
			const centroid = nodes.reduce(
				(acc, node) => ({
					x: acc.x + node.x,
					y: acc.y + node.y,
				}),
				{ x: 0, y: 0 }
			);

			this.centerTo({
				x: centroid.x / nodes.length,
				y: centroid.y / nodes.length,
			});
		}
	}

	/**
	 * Frames a materialised branch without making its nodes feel larger than the
	 * surrounding board. Centering first keeps the seed and all new results in
	 * view; the small follow-up zoom-out gives the branch visual breathing room.
	 */
	focusMaterializedNodes(nodeIds: string[]) {
		this.focusNodesIfNeeded(nodeIds, true);
		window.setTimeout(() => this.scaleBy(0.86), 320);
	}

	getNodeWithLabel(label: string): NCNode | undefined {
		return this.nodes.find((node) => node.label?.toLowerCase() === label.toLowerCase());
	}

	getConnectedNodes(node: NCNode): Array<NCNode> {
		return getAllLinkedNodes([node], this.links).filter((n) => n.uid !== node.uid);
	}

	/**
	 * Function to use with .join to update nodes
	 */
	updateNode(
		nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>
	): d3.Selection<SVGGElement, NCNode, SVGGElement, null> {
		// Measure rect heights first so updateNodes' heart / badge positioning
		// uses the freshly-cached height instead of the previous frame's value.
		updateLabelInput(nodes, this.nodeRadius);
		updateNodes(nodes, this.graphProps);
		return nodes;
	}

	loadNewUserData(userData: Array<BoardUser>) {
		const changedActiveNodeIds = getChangedActiveNodeIds(this.boardUsers, userData);
		this.boardUsers = userData;
		if (changedActiveNodeIds.size > 0) {
			this.appendMenuElements(
				this.groups.nodes!.filter((node) => changedActiveNodeIds.has(node.uid))
			);
		}
		updateUsersOnlineStatus(this.boardUsers);
		updateInspireProgress(this);
	}

	onLoadNewGraphData(callback: null | (() => void) = null) {
		this.afterLoadGraphCallback = callback;
	}

	/**
	 * Update graph with new data from collaborators / self (via ShareDB)
	 */
	loadNewGraphData(
		graphData: NCGraphData,
		preDrawFunction: null | (() => void) = null,
		resetPhysics = true
	) {
		this.updateGraphWithNewData(graphData);

		if (preDrawFunction) {
			preDrawFunction();
		}

		if (this.groups.links && this.groups.nodes && this.groups.groups) {
			this.groups.groups = createGroups(
				this.groups.groups,
				this.nodeGroups,
				this.groupDragBehavior
			);
			this.groups.links = createLinkLines(this.groups.links, this.links, this.graphProps);
			this.groups.nodes = createNodes(
				this.groups.nodes,
				this.nodes,
				this.enterNode.bind(this),
				this.updateNode.bind(this),
				this.isMovable()
			);
		}

		if (resetPhysics) {
			if (!this.isDragging) {
				resetPhysicsMovement(this);
			}
		}

		this.appendMenuElements();
		this.onTick();
		tutorial.updatePosition();

		if (this.afterLoadGraphCallback) {
			const callback = this.afterLoadGraphCallback;
			this.afterLoadGraphCallback = null;
			setTimeout(callback, 0);
		}
	}

	get boardId(): string {
		return document.body.dataset.boardId!;
	}

	isEditable(): boolean {
		return false;
	}

	isMovable(): boolean {
		return true;
	}

	isPhysicsActive(): boolean {
		return true;
	}

	usesStructuralForces(): boolean {
		return true;
	}

	areNodesColoured(): boolean {
		return false;
	}

	areLinksColoured(): boolean {
		return true;
	}

	get activeNode(): NCNode | undefined {
		return this.nodes.find((node) => this.currentUser && this.currentUser.activeNode === node.uid);
	}

	get activeGroup(): NCGroup | undefined {
		return this.nodeGroups.find((group) => group.active);
	}

	get graphData(): NCGraphData {
		return {
			nodes: this.nodes,
			links: this.links,
			groups: this.nodeGroups,
		};
	}

	applyPositioningLayout(_layout: PositioningLayout) {
		// Ideate overrides this to move positioning-map cards from ShareDB.
	}

	isActiveNodeOwnedByCurrentUser() {
		return this.currentUser.colour === this.activeNode?.colour;
	}

	get currentUser(): BoardUser {
		const currentUser = this.boardUsers.find((u) => this.userColourClass === u.colour);
		if (!currentUser) {
			throw new Error('Could not find current user');
		}
		return currentUser;
	}

	get currentUserIndex(): number {
		const currentUserIndex = this.boardUsers.findIndex((u) => this.userColourClass === u.colour);
		if (currentUserIndex === -1) {
			throw new Error('Could not find current user');
		}
		return currentUserIndex;
	}

	get labels(): Array<string> {
		return getLabels(this.nodes);
	}

	getLikedNodes(): Array<NCNode> {
		return this.nodes.filter((n) => n.likes?.[this.userColourClass]);
	}

	getLikedNodeLabels(): Array<string> {
		return getLabels(this.getLikedNodes());
	}

	getCurrentlySelectedText(): string {
		return window.getSelection()?.toString() || '';
	}

	getSelection(): Selection | null {
		return window.getSelection() || null;
	}

	startTutorial(): void {
		const tutorialButton = document.querySelector<HTMLButtonElement>('#tutorial')!;
		tutorialButton.classList.add('tutorialActive');
		console.log('Start tutorial for', this.graphMode);
		disableChatbot();

		// Should be overridden by sub-classes to actually start the tutorial
	}

	startHints(): void {
		// Overridden by sub-classes
		console.log('Start hints for', this.graphMode);
		disableChatbot();
	}

	setHintsShown(): void {
		document
			.querySelector(`#board-stage-switcher li[data-mode='${this.graphMode}']`)!
			.classList.add('completed');
		console.log('Set hints shown for', this.graphMode);
		enableChatbot();
	}

	// Returns if hints have been shown for this graph mode
	getHintsShown(): boolean {
		// Overridden by sub-classes
		return false;
	}

	setPresentationId(presentationId: string): void {
		this.currentUser.settings.presentationId = presentationId;
		setPresentationLink(presentationId, this.boardTitle);
		this.saveUser();
	}

	clearGraph(): void {
		this.nodes = [];
		this.links = [];
		this.nodeGroups = [];
		this.save();
	}

	/**
	 * Adds a new node at or near a specified point.
	 */
	addNodeNearPoint(
		point: Point,
		label: string | undefined,
		src: string | undefined,
		href: string | undefined,
		like = false,
		isVideo = false,
		pdfMetadata?: Pick<
			TopicAnalysis,
			'pdfInsightId' | 'pdfAnalysisId' | 'pdfFileName' | 'pdfProvenance'
		>
	): NCNode {
		const newNodeProposedPoint = findFreeSpace(this.nodes, point, this.nodeRadius);
		const node = this.spawnNewNode(
			{
				...newNodeProposedPoint,
				label,
				src,
				isVideo,
				href,
				like,
				...(pdfMetadata?.pdfAnalysisId ? { style: 'rect' as const } : {}),
				...pdfMetadata,
			},
			null,
			true
		);
		tutorialEvents.addedSingleNode(node);
		return node;
	}

	async addToBoard(
		likedItem: LikedItem,
		positioningOutput?: PositioningOutputSnapshot
	): Promise<NCNode> {
		// Inspire-origin items (hearted from an Inspire node) re-use the same
		// uid and original (x, y) of the source Inspire node, so the resulting
		// Synthesise node stays linked to the Inspire heart for cascade
		// removal. We only do this on the personal Synthesise (moodboard)
		// board — that's where automatic copies live.
		if (
			this.graphMode === 'moodboard' &&
			!this.isCollaborative &&
			shouldReuseInspireSourceNode(likedItem, positioningOutput) &&
			neuroCreate.doc
		) {
			const inspireNode = neuroCreate.doc.data?.inspire?.data?.nodes?.find(
				(n) => n.uid === likedItem.nodeUid
			);
			if (inspireNode) {
				const existing = this.nodes.find((n) => n.uid === inspireNode.uid);
				if (existing) {
					return existing;
				}
				const copy: NCNode = {
					...structuredClone(inspireNode),
					likes: {
						...(inspireNode.likes || {}),
						[nodeUserLikeKey(this.currentUser.id)]: true,
					},
				};
				if (neuroCreate.saveNodes) {
					neuroCreate.saveNodes.create([copy]);
				}
				tutorialEvents.addedSingleNode(copy);
				return copy;
			}
		}

		if (positioningOutput) {
			const snapshot = toReadablePositioningOutput(structuredClone(positioningOutput));
			const existing = this.nodes.find(
				(node) => node.positioningOutput?.sourceOutputId === snapshot.sourceOutputId
			);
			const label = boardLabelForPositioningOutput(snapshot);
			if (existing) {
				if ((existing.positioningOutput?.version || 0) < snapshot.version) {
					existing.positioningOutput = snapshot;
					existing.label = label;
					if (snapshot.payload.kind === 'media') {
						existing.src = snapshot.payload.url;
						existing.isVideo = snapshot.payload.isVideo;
						if (snapshot.payload.isVideo) {
							delete existing.style;
						} else {
							existing.style = undefined;
						}
					} else if (snapshot.payload.kind === 'reference') {
						existing.href = snapshot.payload.url;
						existing.style = 'rect';
					} else if (snapshot.payload.kind !== 'cluster') {
						existing.style = 'rect';
					}
					existing.topics = [
						...new Set([
							...(existing.topics || []),
							'source:idea-box',
							`positioning-output:${snapshot.type}`,
						]),
					];
					neuroCreate.saveNodes?.update([existing]);
				}
				return existing;
			}
			if (snapshot.payload.kind === 'cluster') {
				const newPosition = findFreeSpace(this.nodes, { x: -380, y: 50 }, this.nodeRadius, true);
				const centralNode = this.spawnCluster(snapshot.payload.cluster, {
					...newPosition,
					colourClass: this.userColourClass,
					radius: 20,
				});
				centralNode.positioningOutput = snapshot;
				centralNode.topics = [
					...(centralNode.topics || []),
					'source:idea-box',
					`positioning-output:${snapshot.type}`,
				];
				neuroCreate.saveNodes?.update([centralNode]);
				return centralNode;
			}
			if (snapshot.payload.kind === 'media') {
				const mediaNode = this.addNodeNearPoint(
					{ x: -380, y: 50 },
					undefined,
					snapshot.payload.url,
					undefined,
					false,
					snapshot.payload.isVideo
				);
				mediaNode.positioningOutput = snapshot;
				neuroCreate.saveNodes?.update([mediaNode]);
				return mediaNode;
			}
			if (snapshot.payload.kind === 'reference') {
				const referenceNode = this.addNodeNearPoint(
					{ x: -380, y: 50 },
					label,
					undefined,
					snapshot.payload.url
				);
				referenceNode.positioningOutput = snapshot;
				neuroCreate.saveNodes?.update([referenceNode]);
				return referenceNode;
			}

			const newPosition = findFreeSpace(this.nodes, { x: -380, y: 50 }, this.nodeRadius, true);
			const newNode = this.spawnNewNode(
				{
					...newPosition,
					label,
					style: 'rect',
					positioningOutput: snapshot,
					topics: ['source:idea-box', `positioning-output:${snapshot.type}`],
				},
				null,
				true
			);
			tutorialEvents.addedSingleNode(newNode);
			return newNode;
		}

		let newPosition = { x: -380, y: 50 };
		if (likedItem.type === 'search-image' || likedItem.type.startsWith('image')) {
			return this.addNodeNearPoint(newPosition, undefined, likedItem.url, undefined);
		} else if (likedItem.type.startsWith('video')) {
			return this.addNodeNearPoint(newPosition, undefined, likedItem.url, undefined, false, true);
		} else if (likedItem.type === 'cluster' && likedItem.text) {
			const { group, values } = JSON.parse(likedItem.text) as {
				group: string;
				values: Array<string>;
			};
			newPosition = findFreeSpace(this.nodes, newPosition, this.nodeRadius, true);
			const centralNode = this.spawnCluster(
				{ group, values },
				{ ...newPosition, colourClass: this.userColourClass, radius: 20 }
			);
			return centralNode;
		} else if (likedItem.type === 'search-page' || likedItem.type === 'article') {
			return this.addNodeNearPoint(newPosition, likedItem.text, undefined, likedItem.url);
		} else if (likedItem.type === 'motivations') {
			try {
				const motivations = JSON.parse(likedItem.text!) as Motivations;
				return new Promise<NCNode>((resolve) => {
					showAlert('Which element would you like to add to the board?', [
						...motivations.stakeholders.flatMap((stakeholder) => {
							const buttons: Array<ButtonDefinition> = [];
							buttons.push({
								label: `${stakeholder.name} - Description`,
								minWidth: '320px',
								onClick: () => {
									resolve(
										this.addNodeNearPoint(
											newPosition,
											`${stakeholder.name}: ${stakeholder.description}`,
											undefined,
											undefined
										)
									);
								},
							});
							if (stakeholder.imageUrl && this.graphMode === 'moodboard') {
								buttons.push({
									label: `Image`,
									iconUrl: stakeholder.imageUrl,
									onClick: () => {
										resolve(
											this.addNodeNearPoint(newPosition, undefined, stakeholder.imageUrl, undefined)
										);
									},
								});
							}
							return buttons;
						}),
					]);
				});
			} catch (e) {
				// handle old plain text motivations
				return this.addNodeNearPoint(newPosition, likedItem.text, undefined, undefined);
			}
		} else if (likedItem.type === 'persona') {
			try {
				const persona = JSON.parse(likedItem.text!) as UserPersona;
				if (persona.imageUrl) {
					return new Promise<NCNode>((resolve) => {
						const buttons: Array<ButtonDefinition> = [];
						if (this.graphMode === 'moodboard') {
							buttons.push({
								label: 'Image',
								onClick: () => {
									resolve(
										this.addNodeNearPoint(newPosition, undefined, persona.imageUrl, undefined)
									);
								},
							});
						}
						buttons.push({
							label: 'Text',
							onClick: () => {
								resolve(
									this.addNodeNearPoint(
										newPosition,
										userPersonaToString(persona),
										undefined,
										undefined
									)
								);
							},
						});
						showAlert(
							'Which element of the user persona would you like to add to the board?',
							buttons
						);
					});
				} else {
					return this.addNodeNearPoint(
						newPosition,
						userPersonaToString(persona),
						undefined,
						undefined
					);
				}
			} catch (e) {
				// handle old plain text personas
				return this.addNodeNearPoint(newPosition, likedItem.text, undefined, undefined);
			}
		} else {
			return this.addNodeNearPoint(newPosition, likedItem.text, undefined, undefined);
		}
	}

	viewImage(node: NCNode): void {
		this.viewMedia(node);
	}

	viewMedia(node: NCNode): void {
		if (!node.src) return;
		if (node.isVideo) {
			const src =
				node.src.startsWith('http') && !node.src.includes(window.location.host)
					? `/export-videos?responseType=blob&url=${encodeURIComponent(node.src)}`
					: node.src;
			const modalContent = document.createElement('article');
			modalContent.className = 'pdf-summary-modal pdf-summary-modal--media';
			const video = document.createElement('video');
			video.src = src;
			video.controls = true;
			video.autoplay = true;
			video.playsInline = true;
			video.className = 'pdf-summary-modal__video';
			modalContent.append(video);
			createDialog('', [modalContent], createButtons([{ label: 'Close', primary: true }], true));
			const dialog = document.querySelector<HTMLDialogElement>('main > .alert-show > dialog.alert-window');
			dialog?.classList.add('alert-window--media');
			return;
		}
		showSingleImageLightbox(node.src, node.label || 'Visual reference');
	}

	protected isLocalPdfGraphNode(node: NCNode): boolean {
		return isPdfGraphNode({
			node,
			engine: document.body.dataset.engine,
			selectedFileSummary: this.currentUser.selectedFile?.fileSummary,
		});
	}

	private async preserveAndExpandPdfGraphNode(node: NCNode): Promise<void> {
		try {
			const expansion = await ncApi.expandPdfGraph(this.boardId, {
				analysisId: node.pdfAnalysisId,
				insightId: node.pdfInsightId,
			});
			this.preservePhraseNode(node);
			const sourceTopics = [...new Set([...(node.topics || []), 'source:pdf'])];
			if (
				(expansion.analysisId && node.pdfAnalysisId !== expansion.analysisId) ||
				sourceTopics.length !== (node.topics || []).length
			) {
				node.pdfAnalysisId = expansion.analysisId || node.pdfAnalysisId;
				node.topics = sourceTopics;
				neuroCreate.saveNodes?.update([{ ...node, style: 'rect' }]);
			}
			if (!expansion.results.length) {
				showNonBlockingMessage(
					expansion.diagnostics.fallbackReason === 'analysis_unavailable'
						? 'The local PDF knowledge graph is still being prepared. Please try again shortly.'
						: 'This PDF insight has no deeper knowledge-graph level to expand.',
					[],
					true
				);
				return;
			}

			const existingInsightIds = new Set(
				this.nodes.map((existingNode) => existingNode.pdfInsightId).filter(Boolean)
			);
			for (const insight of expansion.results) {
				if (existingInsightIds.has(insight.insightId)) continue;
				this.addSingleNode(
					node,
					insight.label,
					undefined,
					[
						'source:pdf',
						`pdf-kind:${insight.kind}`,
						...(insight.level ? [`pdf-level:${insight.level}`] : []),
					],
					[],
					undefined,
					false,
					undefined,
					{
						pdfInsightId: insight.insightId,
						pdfAnalysisId: expansion.analysisId,
						pdfFileName: node.pdfFileName,
						pdfProvenance: insight.provenance,
					}
				);
				existingInsightIds.add(insight.insightId);
			}
			this.zoomToFit();
		} catch (error) {
			console.error('Unable to expand the local PDF knowledge graph:', error);
			showNonBlockingMessage(
				'The local PDF knowledge graph could not be expanded. Please try again.',
				[],
				true
			);
		}
	}

	async expand(node: NCNode): Promise<void> {
		if (this.isLocalPdfGraphNode(node)) {
			await this.preserveAndExpandPdfGraphNode(node);
		} else {
			this.checkNodeReadyForAI(node, 'Expand');
			await this.promptExplore(node);
		}
		this.setToolUsed('explore');
	}

	centerOnCentralPoint() {
		const finiteNodes = this.nodes.filter(
			(node) => Number.isFinite(node.x) && Number.isFinite(node.y)
		);
		if (finiteNodes.length === 0) return;
		const centralPoint = getCentralPoint(finiteNodes);
		this.centerTo(centralPoint);
	}

	undo(): void {
		ncApi.undo(this.boardId).then((success) => {
			if (!success) {
				showAlert('No more actions to undo');
			}
		});
	}

	deleteRogueNodes(): Promise<void> {
		// "Rogue nodes" are empty nodes that were typically placed accidentally or forgotten about.
		// We also include nodes without a UID, these should not exist but could be the result of a bug.
		// By automatically deleting these we avoid clutter and ensure that only relevant nodes are shown on page load.
		const rogueNodes = this.nodes.filter((n) => (!n.label && !n.src) || !n.uid);
		if (rogueNodes.length > 0 && neuroCreate.saveNodes) {
			return new Promise<void>((resolve) => {
				neuroCreate.saveNodes!.delete(rogueNodes, () => {
					setTimeout(resolve, 500);
				});
			});
		} else {
			return Promise.resolve();
		}
	}

	centerTo(point: Point) {
		this.deleteRogueNodes();
		super.centerTo(point);
	}

	async zoomToFit(isExporting = false) {
		const mouse = this.mouse;
		if (mouse) {
			// The cursor can interfere with zooming, so we briefly disable it.
			this.mouse = null;
			this.onMouseMoved();
		}

		await this.deleteRogueNodes();

		super.zoomToFit(isExporting);

		if (mouse) {
			this.mouse = mouse;
			this.onMouseMoved();
		}
	}

	// Currently not used. The physics simulation is running on a low 'temperature' which keeps it stable.
	lockLowVelocityNodesInPlace(shouldLock: boolean = true) {
		if (globalThis.neuroCreate.saveNodes && this.isPhysicsActive()) {
			this.nodes = this.nodes.map((n) => {
				if (
					this.currentDraggedNodeCluster?.includes(n) &&
					typeof n.fx !== 'number' &&
					n.vx &&
					n.vy &&
					Math.hypot(n.vx, n.vy) < 5
				) {
					return {
						...n,
						fx: shouldLock ? n.x : undefined,
						fy: shouldLock ? n.y : undefined,
					};
				} else {
					return n;
				}
			});
		}
	}

	lockAllNodesInPlace(shouldLock: boolean = true) {
		if (globalThis.neuroCreate.saveNodes && this.isPhysicsActive()) {
			this.nodes.map((n) => ({
				...n,
				fx: shouldLock ? n.x : undefined,
				fy: shouldLock ? n.y : undefined,
			}));
		}
	}

	isNodeInGroup(node: NCNode): boolean {
		return this.nodeGroups.some((group) => group.nodes.some((n) => n.uid === node.uid));
	}

	getGroupsForNode(node: NCNode): NCGroup[] {
		return this.nodeGroups.filter((group) => group.nodes.some((n) => n.uid === node.uid));
	}

	calculateDraggedNodeCluster(nodes: NCNode[]): void {
		// When a node is dragged, traverse the graph to get all direct neighbors and all connected nodes
		// that have a lower edge count than the current node
		const visited = new Set<string>(); // Track visited nodes
		const clusterNodes: Set<NCNode> = new Set(); // Store nodes to be dragged
		const queue: NCNode[] = Array.from(nodes); // BFS queue
		for (const node of nodes) {
			visited.add(node.uid);
			clusterNodes.add(node);
		}

		this.draggedNodes = nodes;
		while (queue.length > 0) {
			const currentNode = queue.shift()!;
			clusterNodes.add(currentNode);

			// Get all groups that the current node is part of
			const currentNodeGroups = this.getGroupsForNode(currentNode);

			// Get all connected nodes
			const neighborLinks = getLinksInvolvingNode(currentNode, this.links);
			const neighbors = neighborLinks.map((link) =>
				link.source.uid === currentNode.uid ? link.target : link.source
			);
			const currentConnectionCount = neighbors.length;
			for (const neighbor of neighbors) {
				if (!visited.has(neighbor.uid)) {
					const neighborConnectionCount = getLinksInvolvingNode(neighbor, this.links).length;

					// Include neighbor if:
					// 1. It has fewer connections than current node and is closer than 500 to the current node
					// 2. It has 2 or fewer connections
					// AND it's not part of a group (in case of single dragged node)
					// OR it's only part of the same groups as the current node

					const neighborGroups = this.getGroupsForNode(neighbor);
					const isNeighborInSameGroups =
						neighborGroups.every((group) => currentNodeGroups.some((g) => g.uid === group.uid)) &&
						neighborGroups.length === currentNodeGroups.length;

					const distance = getDistanceBetweenPoints(currentNode, neighbor);
					if (
						((neighborConnectionCount < currentConnectionCount &&
							neighborConnectionCount <= 4 &&
							distance < 2000) || // Small clusters that are close enough
							neighborConnectionCount <= 2) && // Individual nodes
						((nodes.length > 1 && (isNeighborInSameGroups || !this.isNodeInGroup(neighbor))) ||
							(nodes.length === 1 && !this.isNodeInGroup(neighbor))) // Dragging a single node, only include if it's not part of a group
					) {
						visited.add(neighbor.uid);
						queue.push(neighbor);
					}
				}
			}
		}

		this.currentDraggedNodeCluster = Array.from(clusterNodes);
	}

	resetDraggedNodeCluster(): void {
		console.log('resetDraggedNodeCluster');
		this.currentDraggedNodeCluster = undefined;
		this.draggedNodes = undefined;
	}

	isSelectingNode(): boolean {
		return this.baseElement.classed('selecting');
	}
}

export default Graph;
